import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Download, Eye, Filter, Calendar, Globe, Users, Shield, Target } from 'lucide-react';
import { unifiedLibraryService } from '@/services/unifiedLibraryService';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { indexedDBService } from '@/services/indexedDBService';
// Removed OrganizationService import - now using user profile data directly
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';
import { subscriptionService } from '@/utils/subscriptionService';
import { useNavigate } from 'react-router-dom';

export const ReportBuilder = () => {
  const [selectedStandards, setSelectedStandards] = useState<string[]>(['GRI']);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['Environmental']);
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['Scope 1']);
  const [reportName, setReportName] = useState('');
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState('2024-12-31');
  const [showPreview, setShowPreview] = useState(false);
  const [userDataPoints, setUserDataPoints] = useState<any[]>([]);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [investments, setInvestments] = useState<any[]>([]);
  const [initiatives, setInitiatives] = useState<any[]>([]);
  const [netZeroTargets, setNetZeroTargets] = useState<any[]>([]);
  const [riskAssessments, setRiskAssessments] = useState<any[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [esgInsights, setEsgInsights] = useState<any>(null);
  const [showEnterpriseUpgradeModal, setShowEnterpriseUpgradeModal] = useState(false);
  const [hasEnterpriseAccess, setHasEnterpriseAccess] = useState<boolean | null>(null);
  const { toast } = useToast();
  const { guardDownload } = useDownloadGate();
  const navigate = useNavigate();

  const standards = ['GRI', 'SASB', 'TCFD', 'IFRS S2', 'CDP', 'SDG'];
  const [categories, setCategories] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const scopes = ['Scope 1', 'Scope 2', 'Scope 3'];

  // Hidden chart refs for PDF capture
  const emissionsChartRef = useRef<HTMLDivElement>(null);
  const waterWasteChartRef = useRef<HTMLDivElement>(null);
  const riskChartRef = useRef<HTMLDivElement>(null);
  const investmentChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      const allMetrics = await unifiedLibraryService.getMetrics();
      const allCategories = [...new Set(allMetrics.map(m => m.category))];
      setMetrics(allMetrics);
      setCategories(allCategories);
    };
    fetchMetrics();
  }, []);

  useEffect(() => {
    const preloadEnterpriseAccess = async () => {
      const [isSuperAdmin, planName] = await Promise.all([
        subscriptionService.isUserSuperAdmin(),
        subscriptionService.getPlanName(),
      ]);
      setHasEnterpriseAccess(isSuperAdmin || planName.toLowerCase() === 'enterprise');
    };

    preloadEnterpriseAccess();
  }, []);

  // Filter metrics based on selections
  const filteredMetrics = useMemo(() => {
    return metrics.filter(metric => {
      const matchesStandards = selectedStandards.some(standard => 
        metric.standards && metric.standards[standard] && 
        (Array.isArray(metric.standards[standard]) ? metric.standards[standard].length > 0 : true)
      );
      const matchesCategories = selectedCategories.length === 0 || selectedCategories.includes(metric.category);
      const matchesScopes = selectedScopes.length === 0 || !metric.scope || selectedScopes.some(scope => 
        metric.scope === scope.toLowerCase().replace(' ', '-')
      );
      
      return matchesStandards && matchesCategories && matchesScopes;
    });
  }, [selectedStandards, selectedCategories, selectedScopes, metrics]);

  const handleStandardChange = (standard: string, checked: boolean) => {
    if (checked) {
      setSelectedStandards([...selectedStandards, standard]);
    } else {
      setSelectedStandards(selectedStandards.filter(s => s !== standard));
    }
  };

  const handleCategoryChange = (category: string, checked: boolean) => {
    if (checked) {
      setSelectedCategories([...selectedCategories, category]);
    } else {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    }
  };

  const handleScopeChange = (scope: string, checked: boolean) => {
    if (checked) {
      setSelectedScopes([...selectedScopes, scope]);
    } else {
      setSelectedScopes(selectedScopes.filter(s => s !== scope));
    }
  };

  const fetchUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await indexedDBService.initialize();
      const filterByDate = (arr: any[]) => arr.filter(a => {
        const d = a.created_at || a.reporting_period || a.assessment_date || a.investment_date;
        if (!d) return true;
        return d >= startDate && d <= endDate;
      });

      const { data: a } = await indexedDBService.getAll<any>('assessments');
      setAssessments(filterByDate((a || []).filter(x => x.user_id === user.id)));
      const { data: s } = await indexedDBService.getAll<any>('suppliers');
      setSuppliers(filterByDate((s || []).filter(x => x.user_id === user.id)));
      const { data: inv } = await indexedDBService.getAll<any>('investments');
      setInvestments(filterByDate((inv || []).filter(x => x.user_id === user.id)));
      const { data: ini } = await indexedDBService.getAll<any>('initiatives');
      setInitiatives(filterByDate((ini || []).filter(x => x.user_id === user.id)));
      const { data: nz } = await indexedDBService.getAll<any>('netZeroTargets');
      setNetZeroTargets(filterByDate((nz || []).filter(x => x.user_id === user.id)));
      const { data: r } = await indexedDBService.getAll<any>('riskAssessments');
      setRiskAssessments(filterByDate((r || []).filter(x => x.user_id === user.id)));

      // Fetch user profile with organization and ESG data
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      setUserProfile(profileData);

      // Generate ESG insights from user profile data
      if (profileData) {
        const insights = generateESGInsights(profileData, {
          suppliers: filterByDate((s || []).filter(x => x.user_id === user.id)),
          assessments: filterByDate((a || []).filter(x => x.user_id === user.id)),
          initiatives: filterByDate((ini || []).filter(x => x.user_id === user.id)),
          netZeroTargets: filterByDate((nz || []).filter(x => x.user_id === user.id)),
          riskAssessments: filterByDate((r || []).filter(x => x.user_id === user.id))
        });
        setEsgInsights(insights);
      }

      // Optional: user metrics from enhanced service could be added here if needed
      setUserDataPoints([]);
    } catch (error) {
      console.error('Error collecting report data:', error);
    }
  };

  const canUseEnterpriseReporting = async () => {
    const [isSuperAdmin, planName] = await Promise.all([
      subscriptionService.isUserSuperAdmin(),
      subscriptionService.getPlanName(),
    ]);

    const allowed = isSuperAdmin || planName.toLowerCase() === 'enterprise';
    setHasEnterpriseAccess(allowed);
    return allowed;
  };

  const handleGenerateReport = async () => {
    if (hasEnterpriseAccess === false) {
      setShowEnterpriseUpgradeModal(true);
      return;
    }

    if (hasEnterpriseAccess === null && !(await canUseEnterpriseReporting())) {
      setShowEnterpriseUpgradeModal(true);
      return;
    }

    if (!reportName.trim()) {
      toast({
        title: "Report Name Required",
        description: "Please enter a name for your report",
        variant: "destructive"
      });
      return;
    }

    await fetchUserData();
    setShowPreview(true);
  };

  const getCategoryIcon = (category: string): JSX.Element => {
    switch (category) {
      case 'Environmental': return (<Globe className="h-4 w-4" />);
      case 'Social': return (<Users className="h-4 w-4" />);
      case 'Governance': return (<Shield className="h-4 w-4" />);
      case 'Economic': return (<Target className="h-4 w-4" />);
      default: return (<Globe className="h-4 w-4" />);
    }
  };

  // Generate ESG insights from user profile data
  const generateESGInsights = (profile: any, data: any) => {
    const companyName = profile.company_name || 'Our organization';
    const industry = profile.industry_sector || 'our industry';
    const companySize = profile.company_size || 'our size';
    
    // Company overview
    let companyOverview = `${companyName} is committed to sustainable growth and responsible business practices.`;
    if (profile.mission_statement) {
      companyOverview += ` Our mission is to ${profile.mission_statement}.`;
    }
    if (profile.esg_policy) {
      companyOverview += ` We have established a comprehensive ESG policy that guides our sustainability approach across all operations.`;
    }
    if (profile.industry_sector) {
      companyOverview += ` As a ${profile.industry_sector} company, we recognize our unique position to drive positive change in our industry.`;
    }
    
    // Industry context
    let industryContext = '';
    if (profile.industry_sector) {
      industryContext = `In the ${profile.industry_sector} sector, we face specific challenges and opportunities related to sustainability.`;
    }
    
    // Commitment strength
    let commitmentStrength = 'We demonstrate strong ESG commitment through our comprehensive approach to sustainability.';
    if (profile.sbti_commitment || profile.carbon_neutral_commitment || profile.net_zero_target_year) {
      commitmentStrength = 'We demonstrate strong ESG commitment through our ambitious climate targets and sustainability initiatives.';
    }
    
    // Key achievements
    let keyAchievements = 'Our key achievements include significant progress in supplier compliance, emissions reduction, and sustainability initiatives.';
    if (data.initiatives && data.initiatives.length > 0) {
      const completedInitiatives = data.initiatives.filter((i: any) => i.status === 'completed');
      if (completedInitiatives.length > 0) {
        const totalReduction = completedInitiatives.reduce((sum: number, i: any) => sum + (i.actual_reduction || 0), 0);
        keyAchievements = `Our key achievements include completing ${completedInitiatives.length} sustainability initiatives, reducing emissions by ${totalReduction.toLocaleString()} tCO2e, and maintaining strong supplier compliance.`;
      }
    }
    
    // Future goals
    let futureGoals = 'Our future sustainability goals focus on scaling decarbonization initiatives, strengthening supplier compliance, and enhancing data transparency.';
    if (profile.net_zero_target_year || profile.carbon_neutral_target_year) {
      futureGoals = `Our future sustainability goals include achieving net-zero emissions by ${profile.net_zero_target_year || 2050}, strengthening supplier compliance, and enhancing data transparency to support our climate commitments.`;
    }
    
    // Enhanced future goals with specific targets
    if (profile.net_zero_target_year || profile.carbon_neutral_target_year || profile.sbti_commitment) {
      const targets = [];
      if (profile.net_zero_target_year) targets.push(`net-zero emissions by ${profile.net_zero_target_year}`);
      if (profile.carbon_neutral_target_year) targets.push(`carbon neutrality by ${profile.carbon_neutral_target_year}`);
      if (profile.sbti_commitment) targets.push('Science Based Targets initiative validation');
      
      futureGoals = `Our ambitious future sustainability goals include: ${targets.join(', ')}. We are building a comprehensive roadmap with clear milestones, investment planning, and progress tracking to achieve these targets.`;
    }
    
    return {
      companyOverview,
      industryContext,
      commitmentStrength,
      keyAchievements,
      futureGoals
    };
  };

  // Get ESG commitment summary from user profile
  const getESGCommitmentSummary = (profile: any) => {
    return {
      hasNetZero: !!profile.net_zero_target_year,
      netZeroYear: profile.net_zero_target_year,
      hasSBTi: !!profile.sbti_commitment,
      sbtiValidationDate: profile.sbti_validation_date,
      hasCarbonNeutral: !!profile.carbon_neutral_commitment,
      carbonNeutralYear: profile.carbon_neutral_target_year
    };
  };

  // Get contact information from user profile
  const getContactInfo = (profile: any) => {
    return {
      name: profile.company_name || 'Our Organization',
      location: profile.headquarters_location || profile.location,
      website: profile.company_website,
      email: profile.email,
      phone: profile.phone
    };
  };

  // Generate standards coverage summary for selected standards and metrics
  const generateStandardsCoverage = (standards: string[], metrics: any[]) => {
    const coverage = {
      totalMetrics: metrics.length,
      totalCategories: new Set(metrics.map(m => m.category)).size,
      standards: {} as any,
      categoryCoverage: {} as Record<string, string[]>
    };

    // Initialize standards coverage
    standards.forEach(standard => {
      coverage.standards[standard] = {
        metrics: 0,
        categories: new Set()
      };
    });

    // Initialize category coverage
    const categories = [...new Set(metrics.map(m => m.category))];
    categories.forEach(category => {
      coverage.categoryCoverage[category] = [];
    });

    // Analyze each metric
    metrics.forEach(metric => {
      let metricHasStandards = false;
      
      standards.forEach(standard => {
        if (metric.standards && metric.standards[standard]) {
          coverage.standards[standard].metrics++;
          coverage.standards[standard].categories.add(metric.category);
          coverage.categoryCoverage[metric.category].push(standard);
          metricHasStandards = true;
        }
      });
      
      // If metric has no standards, add to general category
      if (!metricHasStandards) {
        if (!coverage.categoryCoverage[metric.category]) {
          coverage.categoryCoverage[metric.category] = [];
        }
        coverage.categoryCoverage[metric.category].push('General');
      }
    });

    // Convert Sets to counts for standards
    standards.forEach(standard => {
      coverage.standards[standard].categories = coverage.standards[standard].categories.size;
    });

    // Remove empty categories and sort
    Object.keys(coverage.categoryCoverage).forEach(category => {
      if (coverage.categoryCoverage[category].length === 0) {
        delete coverage.categoryCoverage[category];
      } else {
        // Remove duplicates and sort
        coverage.categoryCoverage[category] = [...new Set(coverage.categoryCoverage[category])].sort();
      }
    });

    return coverage;
  };

  // Derived analytics for charts
  const emissionsByScope = useMemo(() => {
    const sum = (k: string) => assessments.reduce((acc, a) => acc + (Number(a[k]) || 0), 0);
    return [
      { name: 'Scope 1', value: sum('scope1_emissions') },
      { name: 'Scope 2', value: sum('scope2_emissions') },
      { name: 'Scope 3', value: sum('scope3_emissions') },
    ];
  }, [assessments]);

  const waterWasteData = useMemo(() => {
    const water = suppliers.reduce((acc, s) => acc + (Number(s.water_usage) || 0), 0);
    const waste = assessments.reduce((acc, a) => acc + (Number(a.waste_generated_tons) || 0), 0);
    return [
      { name: 'Water (m3)', value: water },
      { name: 'Waste (t)', value: waste },
    ];
  }, [suppliers, assessments]);

  const riskCategoryData = useMemo(() => {
    const cats = ['climate','regulatory','operational','financial','reputational'];
    return cats.map(c => ({ name: c, value: riskAssessments.filter(r => r.risk_category === c).length }));
  }, [riskAssessments]);

  const investmentESG = useMemo(() => {
    const groups: Record<string, number[]> = {};
    (investments || []).forEach((i: any) => {
      const sector = i.sector || 'Unknown';
      const score = Number(i.esg_score) || 0;
      if (!groups[sector]) groups[sector] = [];
      groups[sector].push(score);
    });
    return Object.entries(groups).map(([k, arr]) => ({ name: k, value: arr.length ? Math.round(arr.reduce((a,b)=>a+b,0)/arr.length) : 0 }));
  }, [investments]);

  const COLORS = ['#22c55e', '#3b82f6', '#f97316', '#eab308', '#a855f7', '#ef4444'];

  const captureChart = async (el: HTMLElement | null, pdf: jsPDF, x: number, y: number, maxWidth: number) => {
    if (!el) return y;
    const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = maxWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
    return y + imgHeight + 8;
  };

  const generatePDF = async () => {
    if (hasEnterpriseAccess === false) {
      setShowEnterpriseUpgradeModal(true);
      return;
    }

    if (hasEnterpriseAccess === null && !(await canUseEnterpriseReporting())) {
      setShowEnterpriseUpgradeModal(true);
      return;
    }

    if (!(await guardDownload())) return;

    try {
      await fetchUserData();
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 14;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const contentWidth = pageWidth - margin * 2;
 
      // Header (first page)
      pdf.setFillColor(34, 197, 94);
      pdf.rect(0, 0, pageWidth, 32, 'F');
      pdf.setTextColor(255,255,255);
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(16);
      pdf.text('GreenCard Sustainability Manager', margin, 14);
      pdf.setFontSize(13);
      pdf.text(`${reportName || 'ESG Report'}`, margin, 22);
      pdf.setFontSize(10);
      pdf.text(`${startDate} to ${endDate}`, pageWidth - margin, 28, { align: 'right' });
      pdf.setTextColor(0,0,0);
 
      let y = 40;
      let isFirstSection = true;
      const addSectionTitle = (title: string) => {
        if (!isFirstSection) { pdf.addPage(); y = 20; }
        isFirstSection = false;
        pdf.setFont(undefined, 'bold'); pdf.setFontSize(14); pdf.text(title, margin, y); y += 8;
      };
      const addSubTitle = (title: string) => {
        pdf.addPage(); y = 20;
        pdf.setFont(undefined, 'bold'); pdf.setFontSize(12); pdf.text(title, margin, y); y += 6;
      };
      const addParagraph = (text: string) => {
        pdf.setFont(undefined, 'normal'); pdf.setFontSize(10);
        const lines = pdf.splitTextToSize(text, contentWidth);
        pdf.text(lines, margin, y); y += lines.length * 5 + 2;
      };
      const addKPI = (label: string, value: string) => {
        pdf.setFont(undefined, 'bold'); pdf.setFontSize(11); pdf.text(`${label}: `, margin, y);
        pdf.setFont(undefined, 'normal'); pdf.text(value, margin + 60, y); y += 6;
      };
      const ensureSpace = (h: number) => { if (y + h > 270) { pdf.addPage(); y = 20; } };
      const addTable = (rows: Array<{ key: string; value: string }>) => {
        pdf.setFontSize(10);
        rows.forEach(r => { ensureSpace(6); pdf.text(`${r.key}:`, margin, y); pdf.text(r.value, margin + 60, y); y += 6; });
      };
      const summarize = (arr: any[], label: string, field: string) => `${label}: ${arr.reduce((a,c)=>a + (Number(c[field])||0),0).toLocaleString()}`;
 
      // 1. Executive Summary
      addSectionTitle('1. Executive Summary');
      addSubTitle('1.1 Company Overview & ESG Commitment');
      
      // Use user profile data if available, otherwise fallback to generic
      if (userProfile && esgInsights) {
        addParagraph(esgInsights.companyOverview);
        if (esgInsights.industryContext) {
          addParagraph(esgInsights.industryContext);
        }
        if (esgInsights.commitmentStrength) {
          addParagraph(esgInsights.commitmentStrength);
        }
        
        // Add ESG commitment details
        const commitment = getESGCommitmentSummary(userProfile);
        if (commitment.hasNetZero || commitment.hasSBTi || commitment.hasCarbonNeutral) {
          let commitmentText = 'Our ESG commitments include: ';
          const commitments = [];
          if (commitment.hasNetZero && commitment.netZeroYear) {
            commitments.push(`net-zero emissions by ${commitment.netZeroYear}`);
          }
          if (commitment.hasCarbonNeutral && commitment.carbonNeutralYear) {
            commitments.push(`carbon neutrality by ${commitment.carbonNeutralYear}`);
          }
          if (commitment.hasSBTi) {
            commitments.push('Science Based Targets initiative commitment');
            if (commitment.sbtiValidationDate) {
              commitments.push(`SBTi validation on ${commitment.sbtiValidationDate}`);
            }
          }
          addParagraph(commitmentText + commitments.join(', ') + '.');
        }
      } else {
        addParagraph('Our organization is committed to sustainable growth, climate action, responsible sourcing, and transparent governance. This report summarizes ESG performance across environmental, social, governance, and economic pillars.');
      }
      
      addSubTitle('1.2 Key Achievements & Progress');
      
      if (esgInsights) {
        addParagraph(esgInsights.keyAchievements);
      } else {
        // Enhanced KPIs with year-over-year analysis
        const currentYear = new Date().getFullYear();
        const previousYear = currentYear - 1;
        
        // Basic KPIs
        addKPI('Total Suppliers', String(suppliers.length));
        addKPI('Assessments Completed', String(assessments.filter(a=>a.status==='completed').length));
        addKPI('Initiatives In Progress', String(initiatives.filter(i=>i.status!=='completed').length));
        
        // Enhanced initiative analysis
        const completedInitiatives = initiatives.filter(i => i.status === 'completed');
        const totalReduction = completedInitiatives.reduce((sum, i) => sum + (i.actual_reduction || 0), 0);
        const totalInvestment = completedInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
        const avgROI = completedInitiatives.length > 0 ? 
          completedInitiatives.reduce((sum, i) => sum + (i.roi_percentage || 0), 0) / completedInitiatives.length : 0;
        
        if (completedInitiatives.length > 0) {
          addKPI('Total Emissions Reduced', `${totalReduction.toLocaleString()} tCO2e`);
          addKPI('Total Investment', `$${totalInvestment.toLocaleString()}`);
          addKPI('Average ROI', `${avgROI.toFixed(1)}%`);
        }
        
        // Top 3 achievements from initiatives
        if (completedInitiatives.length > 0) {
          const topAchievements = completedInitiatives
            .sort((a, b) => (b.actual_reduction || 0) - (a.actual_reduction || 0))
            .slice(0, 3);
          
          addParagraph('Top 3 Achievements:');
          topAchievements.forEach((initiative, index) => {
            let achievementText = `${index + 1}. ${initiative.initiative_name}: Reduced ${initiative.actual_reduction?.toLocaleString() || 0} tCO2e`;
            if (initiative.roi_percentage) {
              achievementText += ` with ${initiative.roi_percentage.toFixed(1)}% ROI`;
            }
            addParagraph(achievementText);
          });
        }
        
        // Risk reduction analysis
        const riskReductions = riskAssessments.filter(r => r.mitigation_status === 'implemented');
        if (riskReductions.length > 0) {
          const highRiskReductions = riskReductions.filter(r => r.risk_level === 'high');
          if (highRiskReductions.length > 0) {
            addParagraph(`Significant Risk Reductions: Successfully mitigated ${highRiskReductions.length} high-risk items through proactive measures and enhanced controls.`);
          }
        }
        
        // Year-over-year progress indicators
        addParagraph(`Progress Indicators (${previousYear} vs ${currentYear}):`);
        
        // Supplier compliance improvement
        const currentCompliance = suppliers.length ? 
          suppliers.filter(s => s.environmental_compliance && s.social_compliance && s.governance_compliance).length / suppliers.length * 100 : 0;
        addKPI('Supplier Compliance Rate', `${currentCompliance.toFixed(1)}%`);
        
        // Initiative completion rate
        const completionRate = initiatives.length > 0 ? 
          (completedInitiatives.length / initiatives.length) * 100 : 0;
        addKPI('Initiative Completion Rate', `${completionRate.toFixed(1)}%`);
        
        // Net zero progress
        if (netZeroTargets.length > 0) {
          const activeTargets = netZeroTargets.filter(t => t.status === 'active');
          const onTrackTargets = activeTargets.filter(t => {
            const targetYear = t.target_year || 2050;
            const yearsRemaining = targetYear - currentYear;
            const progress = t.current_progress || 0;
            return progress >= (yearsRemaining / (targetYear - 2020)) * 100;
          });
          addKPI('Net Zero Targets On Track', `${onTrackTargets.length}/${activeTargets.length}`);
        }
      }
      
      addSubTitle('1.3 Future Sustainability Goals');
      
      if (esgInsights) {
        addParagraph(esgInsights.futureGoals);
      } else {
        addParagraph('Scale decarbonization initiatives, strengthen supplier compliance, enhance data transparency, and progress towards net-zero targets aligned with SBTi.');
      }

      // Enhanced Future Goals with Active Targets and Pipeline Analysis
      if (netZeroTargets.length > 0 || initiatives.length > 0) {
        addSubTitle('1.3.1 Active Climate Targets & Milestones');
        
        // Net Zero Targets Summary
        if (netZeroTargets.length > 0) {
          const activeTargets = netZeroTargets.filter(t => t.status === 'active');
          const onTrackTargets = activeTargets.filter(t => {
            const targetYear = t.target_year || 2050;
            const currentYear = new Date().getFullYear();
            const yearsRemaining = targetYear - currentYear;
            const progress = t.current_progress || 0;
            const expectedProgress = ((currentYear - 2020) / (targetYear - 2020)) * 100;
            return progress >= expectedProgress * 0.8; // 80% of expected progress
          });
          
          addParagraph(`Active Climate Targets: ${activeTargets.length} target(s) with ${onTrackTargets.length} on track.`);
          
          // Individual target details
          activeTargets.forEach((target, index) => {
            const targetYear = target.target_year || 2050;
            const currentYear = new Date().getFullYear();
            const yearsRemaining = targetYear - currentYear;
            const progress = target.current_progress || 0;
            const baseline = target.baseline_year || 2020;
            const baselineEmissions = target.baseline_emissions || 0;
            const currentEmissions = target.current_emissions || 0;
            
            let targetText = `${index + 1}. ${target.target_name || 'Climate Target'}: `;
            targetText += `Target: ${targetYear}, Progress: ${progress.toFixed(1)}%, `;
            targetText += `Years Remaining: ${yearsRemaining}`;
            
            if (baseline && baselineEmissions && currentEmissions) {
              const reduction = ((baselineEmissions - currentEmissions) / baselineEmissions) * 100;
              targetText += `, Reduction: ${reduction.toFixed(1)}%`;
            }
            
            addParagraph(targetText);
          });
        }
        
        // Initiative Pipeline Analysis
        if (initiatives.length > 0) {
          addSubTitle('1.3.2 Sustainability Initiative Pipeline');
          
          const plannedInitiatives = initiatives.filter(i => i.status === 'planned');
          const inProgressInitiatives = initiatives.filter(i => i.status === 'in_progress');
          const completedInitiatives = initiatives.filter(i => i.status === 'completed');
          
          addParagraph(`Initiative Pipeline: ${plannedInitiatives.length} planned, ${inProgressInitiatives.length} in progress, ${completedInitiatives.length} completed.`);
          
          // Pipeline summary by category
          const pipelineByCategory = initiatives.reduce((acc: any, initiative) => {
            const category = initiative.category || 'General';
            if (!acc[category]) acc[category] = { planned: 0, in_progress: 0, completed: 0 };
            acc[category][initiative.status] = (acc[category][initiative.status] || 0) + 1;
            return acc;
          }, {});
          
          Object.entries(pipelineByCategory).forEach(([category, counts]: [string, any]) => {
            const total = (counts.planned || 0) + (counts.in_progress || 0) + (counts.completed || 0);
            if (total > 0) {
              addParagraph(`${category}: ${counts.planned || 0} planned, ${counts.in_progress || 0} active, ${counts.completed || 0} completed.`);
            }
          });
          
          // Investment pipeline
          const totalPlannedInvestment = plannedInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          const totalActiveInvestment = inProgressInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          
          if (totalPlannedInvestment > 0 || totalActiveInvestment > 0) {
            addParagraph(`Investment Pipeline: $${totalPlannedInvestment.toLocaleString()} planned, $${totalActiveInvestment.toLocaleString()} actively invested.`);
          }
        }
        
        // Milestone Tracking
        addSubTitle('1.3.3 Key Milestones & Roadmap');
        
        const currentYear = new Date().getFullYear();
        const milestones = [];
        
        // Add profile-based milestones
        if (userProfile?.net_zero_target_year) {
          milestones.push({
            year: userProfile.net_zero_target_year,
            description: 'Net Zero Target',
            type: 'climate'
          });
        }
        if (userProfile?.carbon_neutral_target_year) {
          milestones.push({
            year: userProfile.carbon_neutral_target_year,
            description: 'Carbon Neutrality Target',
            type: 'climate'
          });
        }
        if (userProfile?.sbti_validation_date) {
          const sbtiYear = new Date(userProfile.sbti_validation_date).getFullYear();
          milestones.push({
            year: sbtiYear,
            description: 'SBTi Validation',
            type: 'commitment'
          });
        }
        
        // Add initiative-based milestones
        initiatives.forEach(initiative => {
          if (initiative.target_completion_date) {
            const targetYear = new Date(initiative.target_completion_date).getFullYear();
            milestones.push({
              year: targetYear,
              description: `${initiative.initiative_name} Completion`,
              type: 'initiative'
            });
          }
        });
        
        // Sort and display milestones
        if (milestones.length > 0) {
          const sortedMilestones = milestones
            .filter(m => m.year >= currentYear)
            .sort((a, b) => a.year - b.year);
          
          addParagraph('Upcoming Key Milestones:');
          sortedMilestones.slice(0, 5).forEach(milestone => {
            const yearsAway = milestone.year - currentYear;
            let timelineText = `${milestone.year}: ${milestone.description}`;
            if (yearsAway === 0) timelineText += ' (This year)';
            else if (yearsAway === 1) timelineText += ' (Next year)';
            else timelineText += ` (${yearsAway} years away)`;
            
            addParagraph(timelineText);
          });
        }
        
        // Strategic roadmap
        addParagraph('Strategic Roadmap: Our sustainability journey focuses on three key phases:');
        addParagraph('Phase 1 (Current): Implement active initiatives and achieve short-term targets');
        addParagraph('Phase 2 (2-5 years): Scale successful programs and achieve medium-term milestones');
        addParagraph('Phase 3 (5+ years): Reach long-term climate commitments and industry leadership');
      }
 
      // 2. Introduction
      addSectionTitle('2. Introduction');
      addSubTitle('2.1 Purpose of the Report');
      
      // Enhanced Purpose of Report with personalized content
      let purposeText = '';
      
      // Company identification
      if (userProfile?.company_name) {
        purposeText += `This report provides ${userProfile.company_name}'s stakeholders with a comprehensive overview of our ESG performance and strategic direction. `;
      } else {
        purposeText += 'This report provides our organization\'s stakeholders with a comprehensive overview of our ESG performance and strategic direction. ';
      }
      
      // Sector context
      if (userProfile?.industry_sector) {
        purposeText += `As a ${userProfile.industry_sector} company, `;
      } else {
        purposeText += 'As an organization committed to sustainability, ';
      }
      
      // Standards context
      if (selectedStandards.length > 0) {
        const standardsText = selectedStandards.length === 1 ? 
          `the ${selectedStandards[0]} standard` : 
          `the ${selectedStandards.slice(0, -1).join(', ')} and ${selectedStandards.slice(-1)} standards`;
        purposeText += `we have prepared this report in accordance with ${standardsText}. `;
      } else {
        purposeText += 'we have prepared this comprehensive sustainability report. ';
      }
      
      // Date context
      const startYear = new Date(startDate).getFullYear();
      const endYear = new Date(endDate).getFullYear();
      if (startYear === endYear) {
        purposeText += `This report covers our performance for ${startYear} and `;
      } else {
        purposeText += `This report covers our performance from ${startDate} to ${endDate} and `;
      }
      
      // Purpose details
      purposeText += 'demonstrates our commitment to transparency, accountability, and continuous improvement in environmental, social, and governance practices. ';
      
      // Audience targeting
      if (userProfile?.industry_sector) {
        const sector = userProfile.industry_sector.toLowerCase();
        if (sector.includes('financial') || sector.includes('banking') || sector.includes('investment')) {
          purposeText += 'This report is designed to meet the information needs of investors, regulators, and financial stakeholders who require comprehensive ESG disclosure for risk assessment and investment decision-making. ';
        } else if (sector.includes('manufacturing') || sector.includes('industrial')) {
          purposeText += 'This report is designed to inform customers, suppliers, employees, and communities about our sustainability performance and supply chain responsibility. ';
        } else if (sector.includes('technology') || sector.includes('software')) {
          purposeText += 'This report is designed to demonstrate our commitment to responsible technology development and digital sustainability to customers, partners, and talent. ';
        } else if (sector.includes('energy') || sector.includes('utilities')) {
          purposeText += 'This report is designed to show our transition to clean energy and climate leadership to regulators, communities, and energy consumers. ';
        } else {
          purposeText += 'This report is designed to provide comprehensive ESG information to all stakeholders including investors, customers, employees, and communities. ';
        }
      } else {
        purposeText += 'This report is designed to provide comprehensive ESG information to all stakeholders including investors, customers, employees, and communities. ';
      }
      
      // Materiality and decision-making
      purposeText += 'The information presented supports informed decision-making, enables stakeholder engagement, and demonstrates our progress toward sustainability goals. ';
      
      // Compliance and assurance
      if (selectedStandards.length > 0) {
        purposeText += `By aligning with ${selectedStandards.join(', ')} requirements, this report ensures compliance with international sustainability reporting frameworks and enhances comparability with industry peers.`;
      } else {
        purposeText += 'This report follows best practices in sustainability reporting and provides a foundation for continuous improvement in our ESG performance.';
      }
      
      addParagraph(purposeText);
      
      // Add specific purpose statements for selected standards
      if (selectedStandards.length > 0) {
        addSubTitle('2.1.1 Reporting Standards Alignment');
        
        selectedStandards.forEach(standard => {
          let standardPurpose = '';
          switch (standard) {
            case 'GRI':
              standardPurpose = 'GRI Standards: Provides comprehensive sustainability reporting framework for transparent disclosure of economic, environmental, and social impacts.';
              break;
            case 'SASB':
              standardPurpose = 'SASB Standards: Delivers industry-specific, financially material sustainability information for investors and capital markets.';
              break;
            case 'TCFD':
              standardPurpose = 'TCFD Framework: Discloses climate-related financial risks and opportunities for investors, lenders, and insurance underwriters.';
              break;
            case 'IFRS S2':
              standardPurpose = 'IFRS S2: Reports climate-related risks and opportunities in financial statements for investors and other capital providers.';
              break;
            case 'CDP':
              standardPurpose = 'CDP Disclosure: Provides environmental impact data for investors, purchasers, and policymakers to drive sustainable decision-making.';
              break;
            case 'SDG':
              standardPurpose = 'SDG Alignment: Demonstrates contribution to United Nations Sustainable Development Goals for global sustainability progress.';
              break;
            default:
              standardPurpose = `${standard}: Aligns with international sustainability reporting best practices.`;
          }
          addParagraph(standardPurpose);
        });
      }
      addSubTitle('2.2 Reporting Frameworks & Standards Used');
      
      // Enhanced Standards section with metric mapping and coverage table
      if (selectedStandards.length > 0) {
        // Standards overview
        const standardsText = selectedStandards.length === 1 ? 
          `This report aligns with the ${selectedStandards[0]} standard` : 
          `This report aligns with multiple international standards: ${selectedStandards.join(', ')}`;
        addParagraph(standardsText);
        
        // Standards coverage table
        addSubTitle('2.2.1 Standards Coverage Summary');
        
        // Generate coverage summary for selected standards
        const coverageData = generateStandardsCoverage(selectedStandards, filteredMetrics);
        
        if (coverageData.totalMetrics > 0) {
          addParagraph(`Our ESG reporting covers ${coverageData.totalMetrics} metrics across ${coverageData.totalCategories} categories, with comprehensive alignment to selected standards.`);
          
          // Standards coverage breakdown
          selectedStandards.forEach(standard => {
            const standardCoverage = coverageData.standards[standard];
            if (standardCoverage) {
              const coveragePercent = Math.round((standardCoverage.metrics / coverageData.totalMetrics) * 100);
              addParagraph(`${standard}: ${standardCoverage.metrics} metrics (${coveragePercent}% coverage) - ${standardCoverage.categories} categories`);
            }
          });
          
          // Category coverage
          addParagraph('Category Coverage by Standards:');
          Object.entries(coverageData.categoryCoverage).forEach(([category, standards]) => {
            const standardList = standards.join(', ');
            addParagraph(`${category}: ${standardList}`);
          });
        } else {
          addParagraph('Standards coverage analysis will be available once metrics are selected and data is populated.');
        }
        
        // Detailed standard descriptions
        addSubTitle('2.2.2 Standard-Specific Coverage');
        
        selectedStandards.forEach(standard => {
          const standardCoverage = coverageData.standards[standard];
          if (standardCoverage) {
            addParagraph(`${standard} Standards Coverage:`);
            
            // Show metrics that align with this standard
            const standardMetrics = filteredMetrics.filter(metric => 
              metric.standards && metric.standards[standard]
            );
            
            if (standardMetrics.length > 0) {
              addParagraph(`• ${standardMetrics.length} metrics align with ${standard} requirements`);
              
              // Show top categories for this standard
              const categoryCounts = standardMetrics.reduce((acc: any, metric) => {
                acc[metric.category] = (acc[metric.category] || 0) + 1;
                return acc;
              }, {});
              
              const topCategories = Object.entries(categoryCounts)
                .sort(([,a]: any, [,b]: any) => b - a)
                .slice(0, 3);
              
              if (topCategories.length > 0) {
                const categoryText = topCategories.map(([cat, count]: [string, any]) => 
                  `${cat} (${count})`
                ).join(', ');
                addParagraph(`• Top categories: ${categoryText}`);
              }
            } else {
              addParagraph(`• No specific metrics currently align with ${standard} requirements`);
            }
          }
        });
        
        // Metric-to-standard mapping table
        if (filteredMetrics.length > 0) {
          addSubTitle('2.2.3 Metric-to-Standard Mapping');
          addParagraph('The following table shows how our reported metrics align with international standards:');
          
          // Create mapping table
          const mappingTable = filteredMetrics.map(metric => {
            const standardCodes = [];
            selectedStandards.forEach(standard => {
              if (metric.standards && metric.standards[standard]) {
                const codes = Array.isArray(metric.standards[standard]) ? 
                  metric.standards[standard] : [metric.standards[standard]];
                standardCodes.push(`${standard}: ${codes.join(', ')}`);
              }
            });
            
            return {
              metric: metric.name,
              category: metric.category,
              subcategory: metric.subcategory,
              standards: standardCodes.join('; ') || 'No standard mapping'
            };
          });
          
          // Display mapping information
          mappingTable.slice(0, 10).forEach(item => {
            addParagraph(`${item.metric} (${item.category}): ${item.standards}`);
          });
          
          if (mappingTable.length > 10) {
            addParagraph(`... and ${mappingTable.length - 10} more metrics with standard mappings.`);
          }
        }
        
      } else {
        addParagraph('No specific reporting standards have been selected for this report. The content follows general ESG reporting best practices.');
      }
      addSubTitle('2.3 Materiality Assessment Process');
      addParagraph('Material topics were identified through internal workshops and stakeholder feedback.');
      addSubTitle('2.4 Stakeholder Engagement');
      addParagraph('Engagement with employees, suppliers, customers, investors, and communities informed this report.');
 
      // 3. Environmental Performance
      addSectionTitle('3. Environmental Performance');
      addSubTitle('3.1 Climate Action & Emissions');
      addSubTitle('3.1.1 Greenhouse Gas (GHG) Emissions (Scope 1, 2, 3)');
      
      // Enhanced emissions analysis with trends and intensity metrics
      if (assessments.length > 0) {
        const totalEmissions = emissionsByScope.reduce((sum, scope) => sum + scope.value, 0);
        const scope1Emissions = emissionsByScope[0].value;
        const scope2Emissions = emissionsByScope[1].value;
        const scope3Emissions = emissionsByScope[2].value;
        
        addParagraph(`Total GHG Emissions: ${totalEmissions.toLocaleString()} tCO2e across all scopes.`);
        
        // Scope breakdown with percentages
        if (totalEmissions > 0) {
          const scope1Percent = ((scope1Emissions / totalEmissions) * 100).toFixed(1);
          const scope2Percent = ((scope2Emissions / totalEmissions) * 100).toFixed(1);
          const scope3Percent = ((scope3Emissions / totalEmissions) * 100).toFixed(1);
          
          addParagraph(`Scope Distribution: Scope 1 (${scope1Percent}%), Scope 2 (${scope2Percent}%), Scope 3 (${scope3Percent}%).`);
        }
        
        // Emissions intensity analysis (if we have company data)
        if (userProfile?.company_size) {
          let intensityMetric = '';
          if (userProfile.company_size === 'Small' || userProfile.company_size === 'Medium') {
            intensityMetric = `Emissions per employee: ${(totalEmissions / 100).toFixed(2)} tCO2e/employee (estimated).`;
          } else if (userProfile.company_size === 'Large') {
            intensityMetric = `Emissions per employee: ${(totalEmissions / 1000).toFixed(2)} tCO2e/employee (estimated).`;
          }
          if (intensityMetric) addParagraph(intensityMetric);
        }
        
        // Year-over-year comparison (if we have historical data)
        const currentYear = new Date().getFullYear();
        const previousYear = currentYear - 1;
        addParagraph(`Emissions Analysis (${previousYear} vs ${currentYear}): Our emissions tracking shows comprehensive coverage across all operational scopes.`);
      }
      
      y = await captureChart(emissionsChartRef.current, pdf, margin, y, contentWidth);
      
      addSubTitle('3.1.2 Carbon Reduction Targets (Net-Zero Commitments)');
      
      // Enhanced net-zero analysis
      if (netZeroTargets.length > 0) {
        const activeTargets = netZeroTargets.filter(t => t.status === 'active');
        const completedTargets = netZeroTargets.filter(t => t.status === 'completed');
        const onTrackTargets = activeTargets.filter(t => {
          const targetYear = t.target_year || 2050;
          const yearsRemaining = targetYear - new Date().getFullYear();
          const progress = t.current_progress || 0;
          const expectedProgress = ((new Date().getFullYear() - 2020) / (targetYear - 2020)) * 100;
          return progress >= expectedProgress * 0.8;
        });
        
        addParagraph(`Net-Zero Targets: ${activeTargets.length} active targets with ${onTrackTargets.length} on track. ${completedTargets.length} targets completed.`);
        
        // Target trajectory analysis
        if (activeTargets.length > 0) {
          addParagraph('Target Trajectory Analysis:');
          activeTargets.forEach((target, index) => {
            const targetYear = target.target_year || 2050;
            const yearsRemaining = targetYear - new Date().getFullYear();
            const progress = target.current_progress || 0;
            const baseline = target.baseline_year || 2020;
            const baselineEmissions = target.baseline_emissions || 0;
            const currentEmissions = target.current_emissions || 0;
            
            let trajectoryText = `${index + 1}. ${target.target_name || 'Climate Target'}: `;
            trajectoryText += `Target Year: ${targetYear}, Years Remaining: ${yearsRemaining}, `;
            trajectoryText += `Current Progress: ${progress.toFixed(1)}%`;
            
            if (baseline && baselineEmissions && currentEmissions) {
              const reduction = ((baselineEmissions - currentEmissions) / baselineEmissions) * 100;
              const annualReduction = reduction / (new Date().getFullYear() - baseline);
              trajectoryText += `, Annual Reduction Rate: ${annualReduction.toFixed(1)}%`;
            }
            
            addParagraph(trajectoryText);
          });
        }
      } else {
        addParagraph(`Active Targets: ${netZeroTargets.length}. Progressing initiatives: ${initiatives.length}.`);
      }
      
      addSubTitle('3.1.3 Energy Efficiency & Renewable Energy Usage');
      
      // Enhanced energy analysis
      if (assessments.length > 0 || suppliers.length > 0) {
        const energyData = assessments.filter(a => a.energy_consumption || a.renewable_energy_percentage);
        const supplierEnergy = suppliers.filter(s => s.energy_usage || s.renewable_energy);
        
        if (energyData.length > 0 || supplierEnergy.length > 0) {
          addParagraph('Energy Management Performance:');
          
          // Energy consumption analysis
          const totalEnergy = energyData.reduce((sum, a) => sum + (Number(a.energy_consumption) || 0), 0);
          if (totalEnergy > 0) {
            addParagraph(`Total Energy Consumption: ${totalEnergy.toLocaleString()} MWh tracked across operations.`);
          }
          
          // Renewable energy analysis
          const renewableData = energyData.filter(a => a.renewable_energy_percentage);
          if (renewableData.length > 0) {
            const avgRenewable = renewableData.reduce((sum, a) => sum + (Number(a.renewable_energy_percentage) || 0), 0) / renewableData.length;
            addParagraph(`Renewable Energy Adoption: Average ${avgRenewable.toFixed(1)}% renewable energy usage across tracked operations.`);
          }
          
          // Energy efficiency initiatives
          const efficiencyInitiatives = initiatives.filter(i => 
            i.category === 'Energy' || i.initiative_name?.toLowerCase().includes('energy') || i.initiative_name?.toLowerCase().includes('efficiency')
          );
          if (efficiencyInitiatives.length > 0) {
            addParagraph(`Energy Efficiency Initiatives: ${efficiencyInitiatives.length} active initiatives focused on energy optimization and renewable adoption.`);
          }
        } else {
          addParagraph('Energy consumption initiatives and renewable adoption are tracked across operations.');
        }
      } else {
        addParagraph('Energy consumption initiatives and renewable adoption are tracked across operations.');
      }
      
      addSubTitle('3.2 Resource Management');
      addSubTitle('3.2.1 Water Consumption & Conservation / 3.2.2 Waste Generation & Recycling');
      
      // Enhanced water and waste analysis
      if (suppliers.length > 0 || assessments.length > 0) {
        const waterData = suppliers.filter(s => s.water_usage);
        const wasteData = assessments.filter(a => a.waste_generated_tons || a.recycling_rate);
        
        if (waterData.length > 0) {
          const totalWater = waterData.reduce((sum, s) => sum + (Number(s.water_usage) || 0), 0);
          const avgWater = totalWater / waterData.length;
          addParagraph(`Water Management: ${totalWater.toLocaleString()} m³ total water usage tracked. Average: ${avgWater.toFixed(0)} m³ per supplier.`);
          
          // Water conservation initiatives
          const waterInitiatives = initiatives.filter(i => 
            i.category === 'Water' || i.initiative_name?.toLowerCase().includes('water') || i.initiative_name?.toLowerCase().includes('conservation')
          );
          if (waterInitiatives.length > 0) {
            addParagraph(`Water Conservation: ${waterInitiatives.length} initiatives focused on water efficiency and conservation.`);
          }
        }
        
        if (wasteData.length > 0) {
          const totalWaste = wasteData.reduce((sum, a) => sum + (Number(a.waste_generated_tons) || 0), 0);
          const avgRecycling = wasteData.reduce((sum, a) => sum + (Number(a.recycling_rate) || 0), 0) / wasteData.length;
          
          addParagraph(`Waste Management: ${totalWaste.toLocaleString()} tons waste generated. Average recycling rate: ${avgRecycling.toFixed(1)}%.`);
          
          // Waste reduction initiatives
          const wasteInitiatives = initiatives.filter(i => 
            i.category === 'Waste' || i.initiative_name?.toLowerCase().includes('waste') || i.initiative_name?.toLowerCase().includes('recycling')
          );
          if (wasteInitiatives.length > 0) {
            addParagraph(`Waste Reduction: ${wasteInitiatives.length} initiatives focused on waste minimization and circular economy principles.`);
          }
        }
      }
      
      y = await captureChart(waterWasteChartRef.current, pdf, margin, y, contentWidth);
      
      addSubTitle('3.2.3 Circular Economy Initiatives');
      
      // Enhanced circular economy analysis
      const circularInitiatives = initiatives.filter(i => 
        i.category === 'Circular Economy' || 
        i.initiative_name?.toLowerCase().includes('circular') ||
        i.initiative_name?.toLowerCase().includes('recycling') ||
        i.initiative_name?.toLowerCase().includes('reuse') ||
        i.initiative_name?.toLowerCase().includes('upcycling')
      );
      
      if (circularInitiatives.length > 0) {
        const plannedCircular = circularInitiatives.filter(i => i.status === 'planned').length;
        const activeCircular = circularInitiatives.filter(i => i.status === 'in_progress').length;
        const completedCircular = circularInitiatives.filter(i => i.status === 'completed').length;
        
        addParagraph(`Circular Economy Program: ${circularInitiatives.length} initiatives (${plannedCircular} planned, ${activeCircular} active, ${completedCircular} completed).`);
        
        // Circular economy impact
        const completedCircularInitiatives = circularInitiatives.filter(i => i.status === 'completed');
        if (completedCircularInitiatives.length > 0) {
          const totalWasteReduction = completedCircularInitiatives.reduce((sum, i) => sum + (i.actual_reduction || 0), 0);
          addParagraph(`Circular Economy Impact: ${totalWasteReduction.toLocaleString()} tons of waste diverted through circular initiatives.`);
        }
      } else {
        addParagraph('Programs to reduce waste, increase recycling, and promote circularity are maturing.');
      }
      
      addSubTitle('3.3 Biodiversity & Land Use');
      
      // Enhanced biodiversity analysis
      const biodiversityInitiatives = initiatives.filter(i => 
        i.category === 'Biodiversity' || 
        i.initiative_name?.toLowerCase().includes('biodiversity') ||
        i.initiative_name?.toLowerCase().includes('habitat') ||
        i.initiative_name?.toLowerCase().includes('conservation') ||
        i.initiative_name?.toLowerCase().includes('land use')
      );
      
      if (biodiversityInitiatives.length > 0) {
        addParagraph(`Biodiversity Protection: ${biodiversityInitiatives.length} initiatives focused on habitat conservation and sustainable land use.`);
        
        // Biodiversity impact metrics
        const completedBiodiversity = biodiversityInitiatives.filter(i => i.status === 'completed');
        if (completedBiodiversity.length > 0) {
          const totalAreaProtected = completedBiodiversity.reduce((sum, i) => sum + (i.area_impact || 0), 0);
          if (totalAreaProtected > 0) {
            addParagraph(`Protected Areas: ${totalAreaProtected.toLocaleString()} hectares of habitat protected or restored through biodiversity initiatives.`);
          }
        }
      } else {
        addParagraph('We assess supply chain biodiversity risks and enforce sustainable sourcing policies.');
      }
 
      // 4. Social Responsibility
      addSectionTitle('4. Social Responsibility');
      addSubTitle('4.1 Workforce & Labor Practices');
      
      // Enhanced workforce analysis
      const workforceInitiatives = initiatives.filter(i => 
        i.category === 'Social' || i.category === 'Workforce' ||
        i.initiative_name?.toLowerCase().includes('training') ||
        i.initiative_name?.toLowerCase().includes('safety') ||
        i.initiative_name?.toLowerCase().includes('diversity') ||
        i.initiative_name?.toLowerCase().includes('inclusion')
      );
      
      if (workforceInitiatives.length > 0 || assessments.length > 0) {
        // Safety performance analysis
        const safetyData = assessments.filter(a => a.incidents_count !== undefined);
        if (safetyData.length > 0) {
          const totalIncidents = safetyData.reduce((sum, a) => sum + (Number(a.incidents_count) || 0), 0);
          const avgIncidents = totalIncidents / safetyData.length;
          addParagraph(`Safety Performance: ${totalIncidents} total incidents recorded across ${safetyData.length} assessment periods. Average: ${avgIncidents.toFixed(1)} incidents per period.`);
          
          // Incident trend analysis
          const currentYear = new Date().getFullYear();
          const previousYear = currentYear - 1;
          addParagraph(`Safety Trend Analysis (${previousYear} vs ${currentYear}): Comprehensive incident tracking and prevention measures implemented.`);
        }
        
        // Training and development initiatives
        const trainingInitiatives = workforceInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('training') || 
          i.initiative_name?.toLowerCase().includes('development') ||
          i.initiative_name?.toLowerCase().includes('education')
        );
        
        if (trainingInitiatives.length > 0) {
          const completedTraining = trainingInitiatives.filter(i => i.status === 'completed');
          const activeTraining = trainingInitiatives.filter(i => i.status === 'in_progress');
          addParagraph(`Training & Development: ${trainingInitiatives.length} total programs (${completedTraining.length} completed, ${activeTraining.length} active).`);
          
          // Training investment analysis
          const trainingInvestment = trainingInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          if (trainingInvestment > 0) {
            addParagraph(`Training Investment: $${trainingInvestment.toLocaleString()} invested in workforce development programs.`);
          }
        }
        
        // Diversity, Equity & Inclusion (DEI) initiatives
        const deiInitiatives = workforceInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('diversity') ||
          i.initiative_name?.toLowerCase().includes('inclusion') ||
          i.initiative_name?.toLowerCase().includes('equity') ||
          i.initiative_name?.toLowerCase().includes('dei')
        );
        
        if (deiInitiatives.length > 0) {
          addParagraph(`Diversity, Equity & Inclusion: ${deiInitiatives.length} active DEI initiatives focused on creating an inclusive workplace culture.`);
          
          // DEI impact metrics
          const completedDEI = deiInitiatives.filter(i => i.status === 'completed');
          if (completedDEI.length > 0) {
            addParagraph(`DEI Progress: ${completedDEI.length} DEI programs completed, contributing to enhanced workplace diversity and inclusion.`);
          }
        }
        
        // Employee engagement and wellbeing
        const wellbeingInitiatives = workforceInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('wellbeing') ||
          i.initiative_name?.toLowerCase().includes('wellness') ||
          i.initiative_name?.toLowerCase().includes('engagement') ||
          i.initiative_name?.toLowerCase().includes('mental health')
        );
        
        if (wellbeingInitiatives.length > 0) {
          addParagraph(`Employee Wellbeing: ${wellbeingInitiatives.length} initiatives focused on employee wellness, engagement, and mental health support.`);
        }
      } else {
        addParagraph(`Safety & Training: ${summarize(assessments, 'Total incidents recorded', 'incidents_count')}. Training and development tracked via initiatives.`);
      }
      
      addSubTitle('4.2 Human Rights & Supply Chain Ethics');
      
      // Enhanced supply chain ethics analysis
      if (suppliers.length > 0) {
        const complianceRate = suppliers.length ? Math.round((suppliers.filter((s:any)=>s.environmental_compliance && s.social_compliance && s.governance_compliance).length / suppliers.length) * 100) : 0;
        const environmentalCompliance = suppliers.length ? Math.round((suppliers.filter((s:any)=>s.environmental_compliance).length / suppliers.length) * 100) : 0;
        const socialCompliance = suppliers.length ? Math.round((suppliers.filter((s:any)=>s.social_compliance).length / suppliers.length) * 100) : 0;
        const governanceCompliance = suppliers.length ? Math.round((suppliers.filter((s:any)=>s.governance_compliance).length / suppliers.length) * 100) : 0;
        
        addParagraph(`Supply Chain Compliance Overview: ${complianceRate}% overall compliance rate across ${suppliers.length} suppliers.`);
        addParagraph(`Compliance Breakdown: Environmental (${environmentalCompliance}%), Social (${socialCompliance}%), Governance (${governanceCompliance}%).`);
        
        // Supplier risk assessment
        const highRiskSuppliers = suppliers.filter((s:any) => s.risk_level === 'high' || (!s.environmental_compliance || !s.social_compliance || !s.governance_compliance));
        const mediumRiskSuppliers = suppliers.filter((s:any) => s.risk_level === 'medium');
        const lowRiskSuppliers = suppliers.filter((s:any) => s.risk_level === 'low' && s.environmental_compliance && s.social_compliance && s.governance_compliance);
        
        addParagraph(`Supplier Risk Profile: ${lowRiskSuppliers.length} low-risk, ${mediumRiskSuppliers.length} medium-risk, ${highRiskSuppliers.length} high-risk suppliers.`);
        
        // Human rights initiatives
        const humanRightsInitiatives = initiatives.filter(i => 
          i.category === 'Human Rights' ||
          i.initiative_name?.toLowerCase().includes('human rights') ||
          i.initiative_name?.toLowerCase().includes('labor rights') ||
          i.initiative_name?.toLowerCase().includes('fair trade') ||
          i.initiative_name?.toLowerCase().includes('ethical sourcing')
        );
        
        if (humanRightsInitiatives.length > 0) {
          addParagraph(`Human Rights Programs: ${humanRightsInitiatives.length} initiatives focused on human rights protection and ethical sourcing.`);
          
          const completedHR = humanRightsInitiatives.filter(i => i.status === 'completed');
          if (completedHR.length > 0) {
            addParagraph(`Human Rights Progress: ${completedHR.length} human rights programs completed, strengthening ethical supply chain practices.`);
          }
        }
        
        // Anti-corruption and ethics
        const ethicsInitiatives = initiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('ethics') ||
          i.initiative_name?.toLowerCase().includes('corruption') ||
          i.initiative_name?.toLowerCase().includes('compliance') ||
          i.initiative_name?.toLowerCase().includes('whistleblower')
        );
        
        if (ethicsInitiatives.length > 0) {
          addParagraph(`Ethics & Anti-Corruption: ${ethicsInitiatives.length} programs focused on ethical business practices and anti-corruption measures.`);
        } else {
          addParagraph('Codes of conduct, anti-corruption, and whistleblower policies are enforced across operations.');
        }
      } else {
        const complianceRate = 0;
        addParagraph(`Supplier compliance rate: ${complianceRate}%. Codes of conduct, anti-corruption, and whistleblower policies (where present) are enforced.`);
      }
      
      addSubTitle('4.3 Community Engagement');
      
      // Enhanced community engagement analysis
      const communityInitiatives = initiatives.filter(i => 
        i.category === 'Community' ||
        i.initiative_name?.toLowerCase().includes('community') ||
        i.initiative_name?.toLowerCase().includes('local') ||
        i.initiative_name?.toLowerCase().includes('partnership') ||
        i.initiative_name?.toLowerCase().includes('social impact') ||
        i.initiative_name?.toLowerCase().includes('philanthropy')
      );
      
      if (communityInitiatives.length > 0) {
        const activeCommunity = communityInitiatives.filter(i => i.status === 'in_progress');
        const completedCommunity = communityInitiatives.filter(i => i.status === 'completed');
        
        addParagraph(`Community Investment Program: ${communityInitiatives.length} initiatives (${activeCommunity.length} active, ${completedCommunity.length} completed).`);
        
        // Community investment analysis
        const communityInvestment = communityInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
        if (communityInvestment > 0) {
          addParagraph(`Community Investment: $${communityInvestment.toLocaleString()} invested in community development and social impact programs.`);
        }
        
        // Community impact metrics
        if (completedCommunity.length > 0) {
          const totalCommunityImpact = completedCommunity.reduce((sum, i) => sum + (i.people_impacted || i.beneficiaries || 0), 0);
          if (totalCommunityImpact > 0) {
            addParagraph(`Community Impact: ${totalCommunityImpact.toLocaleString()} people directly benefited from community engagement programs.`);
          }
        }
        
        // Partnership analysis
        const partnerships = communityInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('partnership') ||
          i.initiative_name?.toLowerCase().includes('collaboration')
        );
        
        if (partnerships.length > 0) {
          addParagraph(`Strategic Partnerships: ${partnerships.length} community partnerships established to maximize social impact and local development.`);
        }
        
        // Educational and skills development
        const educationInitiatives = communityInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('education') ||
          i.initiative_name?.toLowerCase().includes('skills') ||
          i.initiative_name?.toLowerCase().includes('training') ||
          i.initiative_name?.toLowerCase().includes('scholarship')
        );
        
        if (educationInitiatives.length > 0) {
          addParagraph(`Educational Support: ${educationInitiatives.length} programs focused on education, skills development, and capacity building in local communities.`);
        }
      } else {
        addParagraph('Community investments and partnerships support local development.');
      }
 
      // 5. Governance & Ethics
      addSectionTitle('5. Governance & Ethics');
      addSubTitle('5.1 Corporate Governance');
      
      // Enhanced corporate governance analysis
      const governanceInitiatives = initiatives.filter(i => 
        i.category === 'Governance' ||
        i.initiative_name?.toLowerCase().includes('governance') ||
        i.initiative_name?.toLowerCase().includes('board') ||
        i.initiative_name?.toLowerCase().includes('transparency') ||
        i.initiative_name?.toLowerCase().includes('disclosure') ||
        i.initiative_name?.toLowerCase().includes('oversight')
      );
      
      if (governanceInitiatives.length > 0 || userProfile) {
        // Board oversight and ESG integration
        if (userProfile?.company_name) {
          addParagraph(`${userProfile.company_name} maintains strong corporate governance practices with comprehensive board oversight across ESG topics and transparent stakeholder disclosures.`);
        } else {
          addParagraph('Board oversight across ESG topics and transparent disclosures are maintained through comprehensive governance frameworks.');
        }
        
        // ESG governance structure
        if (userProfile?.esg_policy) {
          addParagraph('ESG Governance Structure: Formal ESG policy framework guides decision-making processes and ensures accountability across all organizational levels.');
        }
        
        // Governance initiatives analysis
        if (governanceInitiatives.length > 0) {
          const activeGovernance = governanceInitiatives.filter(i => i.status === 'in_progress');
          const completedGovernance = governanceInitiatives.filter(i => i.status === 'completed');
          
          addParagraph(`Governance Enhancement Programs: ${governanceInitiatives.length} initiatives (${activeGovernance.length} active, ${completedGovernance.length} completed) focused on strengthening governance practices.`);
          
          // Governance investment
          const governanceInvestment = governanceInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          if (governanceInvestment > 0) {
            addParagraph(`Governance Investment: $${governanceInvestment.toLocaleString()} invested in governance enhancement and compliance programs.`);
          }
        }
        
        // Transparency and disclosure
        const transparencyInitiatives = governanceInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('transparency') ||
          i.initiative_name?.toLowerCase().includes('disclosure') ||
          i.initiative_name?.toLowerCase().includes('reporting')
        );
        
        if (transparencyInitiatives.length > 0) {
          addParagraph(`Transparency & Disclosure: ${transparencyInitiatives.length} initiatives focused on enhancing transparency and stakeholder communication.`);
        }
        
        // Board composition and diversity (if data available)
        const diversityGovernance = governanceInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('diversity') ||
          i.initiative_name?.toLowerCase().includes('inclusion')
        );
        
        if (diversityGovernance.length > 0) {
          addParagraph(`Board Diversity: ${diversityGovernance.length} initiatives focused on enhancing board diversity and inclusive governance practices.`);
        }
      } else {
        addParagraph('Board oversight across ESG topics and transparent disclosures.');
      }
      
      addSubTitle('5.2 Risk Management & Compliance');
      
      // Enhanced risk management analysis
      if (riskAssessments.length > 0) {
        const totalRisks = riskAssessments.length;
        const highRisks = riskAssessments.filter(r => r.risk_level === 'high').length;
        const mediumRisks = riskAssessments.filter(r => r.risk_level === 'medium').length;
        const lowRisks = riskAssessments.filter(r => r.risk_level === 'low').length;
        
        addParagraph(`Risk Assessment Overview: ${totalRisks} risks identified and assessed (${highRisks} high-risk, ${mediumRisks} medium-risk, ${lowRisks} low-risk).`);
        
        // Risk categories analysis
        const riskCategories = ['climate', 'regulatory', 'operational', 'financial', 'reputational'];
        const categoryAnalysis = riskCategories.map(category => ({
          category,
          count: riskAssessments.filter(r => r.risk_category === category).length,
          mitigated: riskAssessments.filter(r => r.risk_category === category && r.mitigation_status === 'implemented').length
        }));
        
        addParagraph('Risk Category Distribution:');
        categoryAnalysis.forEach(cat => {
          if (cat.count > 0) {
            const mitigationRate = ((cat.mitigated / cat.count) * 100).toFixed(1);
            addParagraph(`• ${cat.category.charAt(0).toUpperCase() + cat.category.slice(1)}: ${cat.count} risks (${mitigationRate}% mitigated)`);
          }
        });
        
        // Risk mitigation progress
        const mitigatedRisks = riskAssessments.filter(r => r.mitigation_status === 'implemented').length;
        const inProgressMitigation = riskAssessments.filter(r => r.mitigation_status === 'in_progress').length;
        const plannedMitigation = riskAssessments.filter(r => r.mitigation_status === 'planned').length;
        
        const mitigationRate = totalRisks > 0 ? ((mitigatedRisks / totalRisks) * 100).toFixed(1) : '0';
        addParagraph(`Risk Mitigation Progress: ${mitigationRate}% of risks mitigated (${mitigatedRisks} implemented, ${inProgressMitigation} in progress, ${plannedMitigation} planned).`);
        
        // Compliance initiatives
        const complianceInitiatives = initiatives.filter(i => 
          i.category === 'Compliance' ||
          i.initiative_name?.toLowerCase().includes('compliance') ||
          i.initiative_name?.toLowerCase().includes('regulatory') ||
          i.initiative_name?.toLowerCase().includes('audit')
        );
        
        if (complianceInitiatives.length > 0) {
          addParagraph(`Compliance Programs: ${complianceInitiatives.length} initiatives focused on regulatory compliance and audit readiness.`);
          
          const completedCompliance = complianceInitiatives.filter(i => i.status === 'completed');
          if (completedCompliance.length > 0) {
            addParagraph(`Compliance Achievement: ${completedCompliance.length} compliance programs completed, strengthening regulatory adherence.`);
          }
        }
        
        // Risk trend analysis
        const currentYear = new Date().getFullYear();
        const previousYear = currentYear - 1;
        addParagraph(`Risk Trend Analysis (${previousYear} vs ${currentYear}): Comprehensive risk monitoring and proactive mitigation strategies implemented.`);
      } else {
        addParagraph('Risk assessments identify climate, regulatory, operational, and reputational risks.');
      }
      
      y = await captureChart(riskChartRef.current, pdf, margin, y, contentWidth);
      
      addSubTitle('5.3 Data Security & Privacy');
      
      // Enhanced data security analysis
      const securityInitiatives = initiatives.filter(i => 
        i.category === 'Security' || i.category === 'Privacy' ||
        i.initiative_name?.toLowerCase().includes('security') ||
        i.initiative_name?.toLowerCase().includes('privacy') ||
        i.initiative_name?.toLowerCase().includes('cyber') ||
        i.initiative_name?.toLowerCase().includes('data protection') ||
        i.initiative_name?.toLowerCase().includes('gdpr') ||
        i.initiative_name?.toLowerCase().includes('compliance')
      );
      
      if (securityInitiatives.length > 0) {
        const activeSecurity = securityInitiatives.filter(i => i.status === 'in_progress');
        const completedSecurity = securityInitiatives.filter(i => i.status === 'completed');
        
        addParagraph(`Cybersecurity & Privacy Program: ${securityInitiatives.length} initiatives (${activeSecurity.length} active, ${completedSecurity.length} completed) focused on data protection and cybersecurity.`);
        
        // Security investment analysis
        const securityInvestment = securityInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
        if (securityInvestment > 0) {
          addParagraph(`Security Investment: $${securityInvestment.toLocaleString()} invested in cybersecurity infrastructure and data protection measures.`);
        }
        
        // Privacy compliance
        const privacyInitiatives = securityInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('privacy') ||
          i.initiative_name?.toLowerCase().includes('gdpr') ||
          i.initiative_name?.toLowerCase().includes('data protection')
        );
        
        if (privacyInitiatives.length > 0) {
          addParagraph(`Privacy Compliance: ${privacyInitiatives.length} programs focused on data privacy regulations and user protection.`);
        }
        
        // Cybersecurity measures
        const cyberInitiatives = securityInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('cyber') ||
          i.initiative_name?.toLowerCase().includes('security') ||
          i.initiative_name?.toLowerCase().includes('threat')
        );
        
        if (cyberInitiatives.length > 0) {
          addParagraph(`Cybersecurity Measures: ${cyberInitiatives.length} initiatives focused on threat detection, prevention, and incident response.`);
        }
        
        // Security training and awareness
        const securityTraining = securityInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('training') ||
          i.initiative_name?.toLowerCase().includes('awareness') ||
          i.initiative_name?.toLowerCase().includes('education')
        );
        
        if (securityTraining.length > 0) {
          addParagraph(`Security Awareness: ${securityTraining.length} programs focused on employee security training and awareness.`);
        }
        
        // Data governance
        const dataGovernance = securityInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('governance') ||
          i.initiative_name?.toLowerCase().includes('management') ||
          i.initiative_name?.toLowerCase().includes('quality')
        );
        
        if (dataGovernance.length > 0) {
          addParagraph(`Data Governance: ${dataGovernance.length} initiatives focused on data quality, lifecycle management, and governance frameworks.`);
        }
      } else {
        addParagraph('We deploy robust cybersecurity and data protection measures.');
      }
 
      // 6. Economic Sustainability
      addSectionTitle('6. Economic Sustainability');
      addSubTitle('6.1 Financial Responsibility');
      
      // Enhanced financial responsibility analysis
      const financialInitiatives = initiatives.filter(i => 
        i.category === 'Financial' || i.category === 'Economic' ||
        i.initiative_name?.toLowerCase().includes('financial') ||
        i.initiative_name?.toLowerCase().includes('economic') ||
        i.initiative_name?.toLowerCase().includes('tax') ||
        i.initiative_name?.toLowerCase().includes('transparency') ||
        i.initiative_name?.toLowerCase().includes('value distribution')
      );
      
      if (financialInitiatives.length > 0 || userProfile || investments.length > 0) {
        // Tax transparency and compliance
        const taxInitiatives = financialInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('tax') ||
          i.initiative_name?.toLowerCase().includes('compliance') ||
          i.initiative_name?.toLowerCase().includes('transparency')
        );
        
        if (taxInitiatives.length > 0) {
          addParagraph(`Tax Transparency: ${taxInitiatives.length} initiatives focused on tax compliance, transparency, and responsible tax practices.`);
        } else if (userProfile?.company_name) {
          addParagraph(`${userProfile.company_name} maintains fair taxation practices, transparent financial reporting, and responsible economic value distribution.`);
        } else {
          addParagraph('Fair taxation, transparent practices, and economic value distribution.');
        }
        
        // Economic value creation and distribution
        if (initiatives.length > 0) {
          const totalInvestment = initiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          const completedInitiatives = initiatives.filter(i => i.status === 'completed');
          const totalROI = completedInitiatives.reduce((sum, i) => sum + (i.roi_percentage || 0), 0);
          const avgROI = completedInitiatives.length > 0 ? (totalROI / completedInitiatives.length) : 0;
          
          if (totalInvestment > 0) {
            addParagraph(`Economic Value Creation: $${totalInvestment.toLocaleString()} total investment across sustainability initiatives with average ROI of ${avgROI.toFixed(1)}%.`);
          }
          
          // Job creation and economic impact
          const economicImpact = initiatives.reduce((sum, i) => sum + (i.jobs_created || 0), 0);
          if (economicImpact > 0) {
            addParagraph(`Economic Impact: ${economicImpact.toLocaleString()} jobs created or supported through sustainability initiatives.`);
          }
        }
        
        // Supplier and local economic support
        if (suppliers.length > 0) {
          const localSuppliers = suppliers.filter(s => s.local_supplier === true || s.region === 'local');
          const localPercentage = suppliers.length > 0 ? ((localSuppliers.length / suppliers.length) * 100).toFixed(1) : '0';
          
          if (localSuppliers.length > 0) {
            addParagraph(`Local Economic Support: ${localPercentage}% of suppliers are local businesses, supporting regional economic development.`);
          }
          
          // Supplier payment practices
          const supplierInvestment = suppliers.reduce((sum, s) => sum + (Number(s.annual_spend) || 0), 0);
          if (supplierInvestment > 0) {
            addParagraph(`Supplier Investment: $${supplierInvestment.toLocaleString()} annual supplier spend supporting economic ecosystem.`);
          }
        }
        
        // Financial transparency initiatives
        const transparencyInitiatives = financialInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('transparency') ||
          i.initiative_name?.toLowerCase().includes('disclosure') ||
          i.initiative_name?.toLowerCase().includes('reporting')
        );
        
        if (transparencyInitiatives.length > 0) {
          addParagraph(`Financial Transparency: ${transparencyInitiatives.length} initiatives focused on financial disclosure and stakeholder transparency.`);
        }
        
        // Sustainable finance and green bonds
        const greenFinanceInitiatives = financialInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('green') ||
          i.initiative_name?.toLowerCase().includes('sustainable finance') ||
          i.initiative_name?.toLowerCase().includes('bond') ||
          i.initiative_name?.toLowerCase().includes('esg financing')
        );
        
        if (greenFinanceInitiatives.length > 0) {
          addParagraph(`Sustainable Finance: ${greenFinanceInitiatives.length} green financing initiatives supporting environmental and social projects.`);
        }
      } else {
        addParagraph('Fair taxation, transparent practices, and economic value distribution.');
      }
      
      addSubTitle('6.2 Sustainable Innovation');
      
      // Enhanced sustainable innovation analysis
      const innovationInitiatives = initiatives.filter(i => 
        i.category === 'Innovation' || i.category === 'R&D' ||
        i.initiative_name?.toLowerCase().includes('innovation') ||
        i.initiative_name?.toLowerCase().includes('research') ||
        i.initiative_name?.toLowerCase().includes('development') ||
        i.initiative_name?.toLowerCase().includes('technology') ||
        i.initiative_name?.toLowerCase().includes('digital') ||
        i.initiative_name?.toLowerCase().includes('green tech')
      );
      
      if (innovationInitiatives.length > 0 || investments.length > 0) {
        // Innovation program overview
        if (innovationInitiatives.length > 0) {
          const activeInnovation = innovationInitiatives.filter(i => i.status === 'in_progress');
          const completedInnovation = innovationInitiatives.filter(i => i.status === 'completed');
          
          addParagraph(`Sustainable Innovation Program: ${innovationInitiatives.length} initiatives (${activeInnovation.length} active, ${completedInnovation.length} completed) focused on sustainable technology and innovation.`);
          
          // Innovation investment analysis
          const innovationInvestment = innovationInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          if (innovationInvestment > 0) {
            addParagraph(`Innovation Investment: $${innovationInvestment.toLocaleString()} invested in sustainable innovation and R&D programs.`);
          }
          
          // Green technology initiatives
          const greenTechInitiatives = innovationInitiatives.filter(i => 
            i.initiative_name?.toLowerCase().includes('green') ||
            i.initiative_name?.toLowerCase().includes('clean') ||
            i.initiative_name?.toLowerCase().includes('renewable') ||
            i.initiative_name?.toLowerCase().includes('sustainable tech')
          );
          
          if (greenTechInitiatives.length > 0) {
            addParagraph(`Green Technology: ${greenTechInitiatives.length} initiatives focused on clean technology development and deployment.`);
          }
          
          // Digital sustainability
          const digitalInitiatives = innovationInitiatives.filter(i => 
            i.initiative_name?.toLowerCase().includes('digital') ||
            i.initiative_name?.toLowerCase().includes('ai') ||
            i.initiative_name?.toLowerCase().includes('automation') ||
            i.initiative_name?.toLowerCase().includes('efficiency')
          );
          
          if (digitalInitiatives.length > 0) {
            addParagraph(`Digital Sustainability: ${digitalInitiatives.length} initiatives leveraging digital technologies for sustainability improvements.`);
          }
        }
        
        // ESG investment portfolio analysis
        if (investments.length > 0) {
          const totalInvestments = investments.length;
          const avgESGScore = investments.reduce((sum, inv) => sum + (Number(inv.esg_score) || 0), 0) / totalInvestments;
          const totalInvestmentValue = investments.reduce((sum, inv) => sum + (Number(inv.investment_amount) || 0), 0);
          
          addParagraph(`ESG Investment Portfolio: ${totalInvestments} investments with average ESG score of ${avgESGScore.toFixed(1)} and total value of $${totalInvestmentValue.toLocaleString()}.`);
          
          // High ESG performers
          const highESGInvestments = investments.filter(inv => Number(inv.esg_score) >= 80);
          if (highESGInvestments.length > 0) {
            const highESGPercentage = ((highESGInvestments.length / totalInvestments) * 100).toFixed(1);
            addParagraph(`High ESG Performance: ${highESGPercentage}% of investments have ESG scores above 80, demonstrating commitment to sustainable investing.`);
          }
          
          // Sector diversification
          const sectors = [...new Set(investments.map(inv => inv.sector))];
          if (sectors.length > 1) {
            addParagraph(`Investment Diversification: Portfolio spans ${sectors.length} sectors, promoting diverse sustainable economic growth.`);
          }
          
          // Green and sustainable investments
          const greenInvestments = investments.filter(inv => 
            inv.investment_type?.toLowerCase().includes('green') ||
            inv.investment_type?.toLowerCase().includes('sustainable') ||
            inv.investment_type?.toLowerCase().includes('esg') ||
            Number(inv.environmental_score) >= 80
          );
          
          if (greenInvestments.length > 0) {
            const greenPercentage = ((greenInvestments.length / totalInvestments) * 100).toFixed(1);
            const greenValue = greenInvestments.reduce((sum, inv) => sum + (Number(inv.investment_amount) || 0), 0);
            addParagraph(`Green Investments: ${greenPercentage}% of portfolio (${greenInvestments.length} investments, $${greenValue.toLocaleString()}) focused on environmental sustainability.`);
          }
        }
        
        // Research and development
        const rdInitiatives = innovationInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('research') ||
          i.initiative_name?.toLowerCase().includes('development') ||
          i.initiative_name?.toLowerCase().includes('r&d')
        );
        
        if (rdInitiatives.length > 0) {
          const rdInvestment = rdInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          addParagraph(`Research & Development: ${rdInitiatives.length} R&D programs with $${rdInvestment.toLocaleString()} investment in sustainable innovation.`);
        }
        
        // Innovation partnerships and collaboration
        const partnershipInitiatives = innovationInitiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('partnership') ||
          i.initiative_name?.toLowerCase().includes('collaboration') ||
          i.initiative_name?.toLowerCase().includes('joint') ||
          i.initiative_name?.toLowerCase().includes('consortium')
        );
        
        if (partnershipInitiatives.length > 0) {
          addParagraph(`Innovation Partnerships: ${partnershipInitiatives.length} collaborative initiatives with partners, universities, and research institutions.`);
        }
        
        // Innovation outcomes and impact
        const completedInnovation = innovationInitiatives.filter(i => i.status === 'completed');
        if (completedInnovation.length > 0) {
          const totalInnovationImpact = completedInnovation.reduce((sum, i) => sum + (i.actual_reduction || i.efficiency_gain || 0), 0);
          if (totalInnovationImpact > 0) {
            addParagraph(`Innovation Impact: Completed programs delivered ${totalInnovationImpact.toLocaleString()} units of measurable sustainability improvement.`);
          }
        }
      } else {
        addParagraph('Sustainable innovation programs focus on green technology development, digital transformation, and ESG-aligned investment strategies.');
      }
      
      y = await captureChart(investmentChartRef.current, pdf, margin, y, contentWidth);
 
      // 7. Performance Metrics & KPIs
      addSectionTitle('7. Performance Metrics & KPIs');
      
      // Enhanced performance metrics with comprehensive analysis
      addParagraph('This section provides comprehensive performance metrics including year-on-year comparisons, progress against targets, and third-party ESG ratings where available.');
      
      addSubTitle('7.1 Key Performance Indicators (KPIs)');
      
      // Core ESG KPIs
      if (assessments.length > 0 || suppliers.length > 0 || initiatives.length > 0) {
        addParagraph('Core ESG Performance Metrics:');
        
        // Environmental KPIs
        if (assessments.length > 0) {
          const totalEmissions = emissionsByScope.reduce((sum, scope) => sum + scope.value, 0);
          const scope1Emissions = emissionsByScope[0].value;
          const scope2Emissions = emissionsByScope[1].value;
          const scope3Emissions = emissionsByScope[2].value;
          
          addKPI('Total GHG Emissions (tCO2e)', totalEmissions.toLocaleString());
          addKPI('Scope 1 Emissions (tCO2e)', scope1Emissions.toLocaleString());
          addKPI('Scope 2 Emissions (tCO2e)', scope2Emissions.toLocaleString());
          addKPI('Scope 3 Emissions (tCO2e)', scope3Emissions.toLocaleString());
          
          // Emissions intensity if company size available
          if (userProfile?.company_size) {
            let intensityMetric = '';
            if (userProfile.company_size === 'Small' || userProfile.company_size === 'Medium') {
              intensityMetric = (totalEmissions / 100).toFixed(2);
            } else if (userProfile.company_size === 'Large') {
              intensityMetric = (totalEmissions / 1000).toFixed(2);
            }
            if (intensityMetric) {
              addKPI('Emissions per Employee (tCO2e)', intensityMetric);
            }
          }
        }
        
        // Social KPIs
        if (suppliers.length > 0) {
          const complianceRate = suppliers.length ? Math.round((suppliers.filter((s:any)=>s.environmental_compliance && s.social_compliance && s.governance_compliance).length / suppliers.length) * 100) : 0;
          addKPI('Supplier Compliance Rate (%)', complianceRate.toString());
          addKPI('Total Suppliers', suppliers.length.toString());
          
          // Local supplier percentage
          const localSuppliers = suppliers.filter(s => s.local_supplier === true || s.region === 'local');
          const localPercentage = suppliers.length > 0 ? ((localSuppliers.length / suppliers.length) * 100).toFixed(1) : '0';
          addKPI('Local Suppliers (%)', localPercentage);
        }
        
        // Governance KPIs
        if (riskAssessments.length > 0) {
          const totalRisks = riskAssessments.length;
          const mitigatedRisks = riskAssessments.filter(r => r.mitigation_status === 'implemented').length;
          const mitigationRate = totalRisks > 0 ? ((mitigatedRisks / totalRisks) * 100).toFixed(1) : '0';
          
          addKPI('Total Risks Identified', totalRisks.toString());
          addKPI('Risk Mitigation Rate (%)', mitigationRate);
          
          // Risk by category
          const riskCategories = ['climate', 'regulatory', 'operational', 'financial', 'reputational'];
          riskCategories.forEach(category => {
            const count = riskAssessments.filter(r => r.risk_category === category).length;
            if (count > 0) {
              addKPI(`${category.charAt(0).toUpperCase() + category.slice(1)} Risks`, count.toString());
            }
          });
        }
        
        // Economic KPIs
        if (initiatives.length > 0) {
          const totalInvestment = initiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          const completedInitiatives = initiatives.filter(i => i.status === 'completed');
          const totalROI = completedInitiatives.reduce((sum, i) => sum + (i.roi_percentage || 0), 0);
          const avgROI = completedInitiatives.length > 0 ? (totalROI / completedInitiatives.length) : 0;
          
          addKPI('Total Sustainability Investment ($)', totalInvestment.toLocaleString());
          addKPI('Completed Initiatives', completedInitiatives.length.toString());
          addKPI('Average ROI (%)', avgROI.toFixed(1));
          
          // Emissions reduction
          const totalReduction = completedInitiatives.reduce((sum, i) => sum + (i.actual_reduction || 0), 0);
          if (totalReduction > 0) {
            addKPI('Total Emissions Reduced (tCO2e)', totalReduction.toLocaleString());
          }
        }
        
        // Investment portfolio KPIs
        if (investments.length > 0) {
          const avgESGScore = investments.reduce((sum, inv) => sum + (Number(inv.esg_score) || 0), 0) / investments.length;
          const totalInvestmentValue = investments.reduce((sum, inv) => sum + (Number(inv.investment_amount) || 0), 0);
          
          addKPI('Average ESG Score', avgESGScore.toFixed(1));
          addKPI('Total Investment Value ($)', totalInvestmentValue.toLocaleString());
          addKPI('Investment Portfolio Size', investments.length.toString());
        }
      }
      
      addSubTitle('7.2 Year-over-Year Performance Trends');
      
      // Enhanced trend analysis
      const currentYearTrends = new Date().getFullYear();
      const previousYear = currentYearTrends - 1;
      
      addParagraph(`Performance Trend Analysis (${previousYear} vs ${currentYearTrends}):`);
      
      if (assessments.length > 0 || initiatives.length > 0) {
        // Emissions trends
        if (assessments.length > 0) {
          addParagraph('Emissions Performance Trends:');
          addParagraph('• Comprehensive emissions tracking across all operational scopes');
          addParagraph('• Year-over-year comparison framework established for continuous improvement');
          addParagraph('• Emissions intensity metrics tracked for operational efficiency');
        }
        
        // Initiative trends
        if (initiatives.length > 0) {
          const currentYearInitiatives = initiatives.filter(i => {
            const year = new Date(i.created_at || i.start_date || Date.now()).getFullYear();
            return year === currentYearTrends;
          });
          const previousYearInitiatives = initiatives.filter(i => {
            const year = new Date(i.created_at || i.start_date || Date.now()).getFullYear();
            return year === previousYear;
          });
          
          addParagraph('Initiative Performance Trends:');
          addParagraph(`• ${currentYearTrends}: ${currentYearInitiatives.length} initiatives launched`);
          addParagraph(`• ${previousYear}: ${previousYearInitiatives.length} initiatives launched`);
          
          if (currentYearInitiatives.length > 0 && previousYearInitiatives.length > 0) {
            const currentYearInvestment = currentYearInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
            const previousYearInvestment = previousYearInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
            
            if (currentYearInvestment > 0 && previousYearInvestment > 0) {
              const investmentChange = ((currentYearInvestment - previousYearInvestment) / previousYearInvestment * 100).toFixed(1);
              addParagraph(`• Investment Change: ${investmentChange}% year-over-year`);
            }
          }
        }
        
        // Supplier compliance trends
        if (suppliers.length > 0) {
          addParagraph('Supply Chain Performance Trends:');
          addParagraph('• Supplier compliance rates tracked and improved over time');
          addParagraph('• Risk assessment and mitigation progress continuously monitored');
          addParagraph('• Local supplier engagement enhanced for regional economic impact');
        }
        
        // Risk management trends
        if (riskAssessments.length > 0) {
          addParagraph('Risk Management Performance Trends:');
          addParagraph('• Risk identification and assessment processes continuously improved');
          addParagraph('• Mitigation strategies implemented with measurable progress');
          addParagraph('• Proactive risk management approach strengthened');
        }
      } else {
        addParagraph('Performance trend analysis will be available as data accumulates over multiple reporting periods.');
      }
      
      addSubTitle('7.3 Target Progress & Achievement');
      
      // Target progress analysis
      if (netZeroTargets.length > 0 || initiatives.length > 0) {
        addParagraph('Target Achievement Progress:');
        
        // Net zero targets
        if (netZeroTargets.length > 0) {
          const activeTargets = netZeroTargets.filter(t => t.status === 'active');
          const completedTargets = netZeroTargets.filter(t => t.status === 'completed');
          const onTrackTargets = activeTargets.filter(t => {
            const targetYear = t.target_year || 2050;
            const yearsRemaining = targetYear - currentYear;
            const progress = t.current_progress || 0;
            const expectedProgress = ((currentYear - 2020) / (targetYear - 2020)) * 100;
            return progress >= expectedProgress * 0.8;
          });
          
          addParagraph(`• Net Zero Targets: ${activeTargets.length} active, ${completedTargets.length} completed, ${onTrackTargets.length} on track`);
          
          // Individual target progress
          activeTargets.forEach((target, index) => {
            const targetYear = target.target_year || 2050;
            const progress = target.current_progress || 0;
            const yearsRemaining = targetYear - currentYear;
            
            addParagraph(`  - ${target.target_name || `Target ${index + 1}`}: ${progress.toFixed(1)}% complete, ${yearsRemaining} years remaining`);
          });
        }
        
        // Initiative targets
        if (initiatives.length > 0) {
          const plannedInitiatives = initiatives.filter(i => i.status === 'planned');
          const inProgressInitiatives = initiatives.filter(i => i.status === 'in_progress');
          const completedInitiatives = initiatives.filter(i => i.status === 'completed');
          
          const completionRate = initiatives.length > 0 ? ((completedInitiatives.length / initiatives.length) * 100).toFixed(1) : '0';
          
          addParagraph(`• Initiative Completion Rate: ${completionRate}% (${completedInitiatives.length}/${initiatives.length})`);
          addParagraph(`• Pipeline Status: ${plannedInitiatives.length} planned, ${inProgressInitiatives.length} in progress`);
        }
        
        // Supplier targets
        if (suppliers.length > 0) {
          const currentCompliance = suppliers.length ? 
            suppliers.filter(s => s.environmental_compliance && s.social_compliance && s.governance_compliance).length / suppliers.length * 100 : 0;
          
          addParagraph(`• Supplier Compliance Target: ${currentCompliance.toFixed(1)}% achieved`);
          
          if (currentCompliance < 100) {
            const nonCompliantSuppliers = suppliers.length - suppliers.filter(s => s.environmental_compliance && s.social_compliance && s.governance_compliance).length;
            addParagraph(`• Improvement Opportunity: ${nonCompliantSuppliers} suppliers require compliance enhancement`);
          }
        }
      } else {
        addParagraph('Target progress tracking will be available as sustainability targets and initiatives are established.');
      }
      
      // Performance summary table
      addParagraph('Performance Summary Table:');
      addTable([
        { key: 'GHG Scope 1', value: emissionsByScope[0].value.toLocaleString() },
        { key: 'GHG Scope 2', value: emissionsByScope[1].value.toLocaleString() },
        { key: 'GHG Scope 3', value: emissionsByScope[2].value.toLocaleString() },
        { key: 'Supplier Compliance Rate (%)', value: suppliers.length ? Math.round((suppliers.filter((s:any)=>s.environmental_compliance && s.social_compliance && s.governance_compliance).length / suppliers.length) * 100).toString() : '0' },
        { key: 'Risk Mitigation Rate (%)', value: riskAssessments.length > 0 ? ((riskAssessments.filter(r => r.mitigation_status === 'implemented').length / riskAssessments.length) * 100).toFixed(1) : '0' },
        { key: 'Initiative Completion Rate (%)', value: initiatives.length > 0 ? ((initiatives.filter(i => i.status === 'completed').length / initiatives.length) * 100).toFixed(1) : '0' },
        { key: 'Avg ESG Investment Score', value: investments.length > 0 ? (investments.reduce((sum, inv) => sum + (Number(inv.esg_score) || 0), 0) / investments.length).toFixed(1) : 'N/A' },
      ]);
 
      // 8. Case Studies & Highlights
      addSectionTitle('8. Case Studies & Highlights');
      
      // Enhanced case studies and highlights with comprehensive analysis
      addParagraph('This section showcases key projects, awards, and success stories that demonstrate our ESG leadership and commitment to sustainability excellence.');
      
      addSubTitle('8.1 Top Sustainability Initiatives');
      
      // Top initiatives analysis
      if (initiatives.length > 0) {
        // Top performing initiatives by impact
        const topImpactInitiatives = initiatives
          .filter(i => i.actual_reduction || i.efficiency_gain || i.roi_percentage)
          .sort((a, b) => {
            const aImpact = (a.actual_reduction || 0) + (a.efficiency_gain || 0) + (a.roi_percentage || 0);
            const bImpact = (b.actual_reduction || 0) + (b.efficiency_gain || 0) + (b.roi_percentage || 0);
            return bImpact - aImpact;
          })
          .slice(0, 5);
        
        if (topImpactInitiatives.length > 0) {
          addParagraph('Top 5 High-Impact Sustainability Initiatives:');
          
          topImpactInitiatives.forEach((initiative, index) => {
            let initiativeText = `${index + 1}. ${initiative.initiative_name || 'Sustainability Initiative'}`;
            
            if (initiative.category) {
              initiativeText += ` (${initiative.category})`;
            }
            
            if (initiative.actual_reduction) {
              initiativeText += ` - Reduced ${initiative.actual_reduction.toLocaleString()} tCO2e`;
            }
            
            if (initiative.efficiency_gain) {
              initiativeText += ` - Achieved ${initiative.efficiency_gain.toLocaleString()}% efficiency improvement`;
            }
            
            if (initiative.roi_percentage) {
              initiativeText += ` - ${initiative.roi_percentage.toFixed(1)}% ROI`;
            }
            
            if (initiative.status === 'completed') {
              initiativeText += ' - COMPLETED';
            } else if (initiative.status === 'in_progress') {
              initiativeText += ' - IN PROGRESS';
            }
            
            addParagraph(initiativeText);
          });
        }
        
        // Initiative success stories by category
        const categories = [...new Set(initiatives.map(i => i.category))].filter(Boolean);
        if (categories.length > 0) {
          addParagraph('Success Stories by Category:');
          
          categories.forEach(category => {
            const categoryInitiatives = initiatives.filter(i => i.category === category && i.status === 'completed');
            if (categoryInitiatives.length > 0) {
              const totalInvestment = categoryInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
              const totalReduction = categoryInitiatives.reduce((sum, i) => sum + (i.actual_reduction || 0), 0);
              
              addParagraph(`• ${category}: ${categoryInitiatives.length} completed initiatives, $${totalInvestment.toLocaleString()} invested, ${totalReduction.toLocaleString()} tCO2e reduced`);
            }
          });
        }
        
        // Innovation highlights
        const innovativeInitiatives = initiatives.filter(i => 
          i.initiative_name?.toLowerCase().includes('innovation') ||
          i.initiative_name?.toLowerCase().includes('technology') ||
          i.initiative_name?.toLowerCase().includes('digital') ||
          i.initiative_name?.toLowerCase().includes('ai') ||
          i.initiative_name?.toLowerCase().includes('automation')
        );
        
        if (innovativeInitiatives.length > 0) {
          addParagraph(`Innovation Leadership: ${innovativeInitiatives.length} cutting-edge initiatives leveraging advanced technologies for sustainability improvements.`);
        }
        
        // Cost savings and ROI highlights
        const highROIInitiatives = initiatives.filter(i => i.roi_percentage && i.roi_percentage > 20);
        if (highROIInitiatives.length > 0) {
          const avgROI = highROIInitiatives.reduce((sum, i) => sum + (i.roi_percentage || 0), 0) / highROIInitiatives.length;
          addParagraph(`Financial Excellence: ${highROIInitiatives.length} initiatives achieved above 20% ROI, with average return of ${avgROI.toFixed(1)}%.`);
        }
      } else {
        addParagraph('Sustainability initiatives and success stories will be featured as programs are implemented and completed.');
      }
      
      addSubTitle('8.2 Awards & Recognition');
      
      // Awards and recognition analysis
      const awardInitiatives = initiatives.filter(i => 
        i.initiative_name?.toLowerCase().includes('award') ||
        i.initiative_name?.toLowerCase().includes('recognition') ||
        i.initiative_name?.toLowerCase().includes('certification') ||
        i.initiative_name?.toLowerCase().includes('accreditation')
      );
      
      if (awardInitiatives.length > 0 || userProfile) {
        addParagraph('ESG Excellence Recognition:');
        
        if (awardInitiatives.length > 0) {
          addParagraph(`• ${awardInitiatives.length} initiatives recognized for excellence in sustainability`);
          
          // Award categories
          const awardCategories = awardInitiatives.map(i => i.category).filter(Boolean);
          if (awardCategories.length > 0) {
            const uniqueCategories = [...new Set(awardCategories)];
            addParagraph(`• Recognition across ${uniqueCategories.length} categories: ${uniqueCategories.join(', ')}`);
          }
        }
        
        // Company-level achievements
        if (userProfile?.company_name) {
          addParagraph(`• ${userProfile.company_name} recognized for ESG leadership and sustainability commitment`);
        }
        
        // SBTi and climate commitments
        if (userProfile?.sbti_commitment) {
          addParagraph('• Science Based Targets initiative commitment recognized for climate leadership');
          if (userProfile?.sbti_validation_date) {
            addParagraph(`• SBTi validation achieved on ${userProfile.sbti_validation_date}`);
          }
        }
        
        // Net zero commitments
        if (userProfile?.net_zero_target_year) {
          addParagraph(`• Net-zero commitment by ${userProfile.net_zero_target_year} recognized for ambitious climate action`);
        }
        
        // Industry leadership
        if (userProfile?.industry_sector) {
          addParagraph(`• Industry leadership in ${userProfile.industry_sector} sustainability practices`);
        }
        
        // Standards compliance
        if (selectedStandards.length > 0) {
          addParagraph(`• Excellence in ${selectedStandards.join(', ')} standards compliance and reporting`);
        }
      } else {
        addParagraph('Awards and recognition will be featured as our ESG programs receive industry and stakeholder recognition.');
      }
      
      addSubTitle('8.3 Success Stories & Impact Narratives');
      
      // Success stories and impact narratives
      if (initiatives.length > 0 || suppliers.length > 0 || riskAssessments.length > 0) {
        addParagraph('Impact Stories and Stakeholder Benefits:');
        
        // Environmental impact stories
        const environmentalInitiatives = initiatives.filter(i => 
          i.category === 'Environmental' || 
          i.actual_reduction > 0 ||
          i.initiative_name?.toLowerCase().includes('emissions') ||
          i.initiative_name?.toLowerCase().includes('energy') ||
          i.initiative_name?.toLowerCase().includes('water') ||
          i.initiative_name?.toLowerCase().includes('waste')
        );
        
        if (environmentalInitiatives.length > 0) {
          const totalEnvironmentalReduction = environmentalInitiatives.reduce((sum, i) => sum + (i.actual_reduction || 0), 0);
          addParagraph(`• Environmental Leadership: ${environmentalInitiatives.length} initiatives delivered ${totalEnvironmentalReduction.toLocaleString()} tCO2e reduction, equivalent to removing ${Math.round(totalEnvironmentalReduction / 4.6)} cars from the road annually`);
        }
        
        // Social impact stories
        const socialInitiatives = initiatives.filter(i => 
          i.category === 'Social' ||
          i.initiative_name?.toLowerCase().includes('community') ||
          i.initiative_name?.toLowerCase().includes('training') ||
          i.initiative_name?.toLowerCase().includes('diversity') ||
          i.initiative_name?.toLowerCase().includes('wellbeing')
        );
        
        if (socialInitiatives.length > 0) {
          const totalSocialInvestment = socialInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          addParagraph(`• Social Impact: ${socialInitiatives.length} initiatives invested $${totalSocialInvestment.toLocaleString()} in community development, workforce enhancement, and social responsibility programs`);
        }
        
        // Supply chain success stories
        if (suppliers.length > 0) {
          const compliantSuppliers = suppliers.filter(s => s.environmental_compliance && s.social_compliance && s.governance_compliance);
          const complianceRate = suppliers.length > 0 ? ((compliantSuppliers.length / suppliers.length) * 100).toFixed(1) : '0';
          
          addParagraph(`• Supply Chain Excellence: ${complianceRate}% supplier compliance rate achieved through comprehensive ESG standards implementation and continuous improvement programs`);
          
          if (compliantSuppliers.length > 0) {
            addParagraph(`• ${compliantSuppliers.length} suppliers successfully aligned with our ESG standards, creating a sustainable and responsible supply chain ecosystem`);
          }
        }
        
        // Risk management success stories
        if (riskAssessments.length > 0) {
          const mitigatedRisks = riskAssessments.filter(r => r.mitigation_status === 'implemented');
          const mitigationRate = riskAssessments.length > 0 ? ((mitigatedRisks.length / riskAssessments.length) * 100).toFixed(1) : '0';
          
          addParagraph(`• Risk Management Excellence: ${mitigationRate}% risk mitigation rate achieved through proactive identification, assessment, and implementation of comprehensive mitigation strategies`);
          
          if (mitigatedRisks.length > 0) {
            const highRiskMitigated = mitigatedRisks.filter(r => r.risk_level === 'high').length;
            if (highRiskMitigated > 0) {
              addParagraph(`• ${highRiskMitigated} high-risk items successfully mitigated, demonstrating our commitment to operational excellence and stakeholder protection`);
            }
          }
        }
        
        // Financial success stories
        if (initiatives.length > 0) {
          const completedInitiatives = initiatives.filter(i => i.status === 'completed');
          if (completedInitiatives.length > 0) {
            const totalInvestment = completedInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
            const totalROI = completedInitiatives.reduce((sum, i) => sum + (i.roi_percentage || 0), 0);
            const avgROI = totalROI / completedInitiatives.length;
            
            addParagraph(`• Financial Performance: $${totalInvestment.toLocaleString()} invested in sustainability initiatives with average ROI of ${avgROI.toFixed(1)}%, demonstrating that sustainability and profitability go hand in hand`);
          }
        }
        
        // Stakeholder engagement success
        if (userProfile?.company_name) {
          addParagraph(`• Stakeholder Engagement: ${userProfile.company_name} has successfully engaged with investors, customers, employees, suppliers, and communities to build a comprehensive ESG strategy that addresses material topics and creates shared value`);
        }
        
        // Future impact potential
        const plannedInitiatives = initiatives.filter(i => i.status === 'planned');
        if (plannedInitiatives.length > 0) {
          const plannedInvestment = plannedInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          addParagraph(`• Future Impact Pipeline: $${plannedInvestment.toLocaleString()} in planned sustainability investments will deliver additional environmental, social, and governance benefits in the coming years`);
        }
      } else {
        addParagraph('Success stories and impact narratives will be developed as our sustainability programs mature and deliver measurable outcomes.');
      }
 
      // 9. Challenges & Future Roadmap
      addSectionTitle('9. Challenges & Future Roadmap');
      
      // Enhanced challenges and roadmap with comprehensive analysis
      addParagraph('This section addresses key challenges encountered in our sustainability journey and outlines our strategic roadmap for continued ESG leadership and impact.');
      
      addSubTitle('9.1 Key Challenges & Lessons Learned');
      
      // Challenges analysis
      if (riskAssessments.length > 0 || initiatives.length > 0 || suppliers.length > 0) {
        addParagraph('Sustainability Journey Challenges:');
        
        // Risk-based challenges
        if (riskAssessments.length > 0) {
          const unmitigatedRisks = riskAssessments.filter(r => r.mitigation_status !== 'implemented');
          const highUnmitigatedRisks = unmitigatedRisks.filter(r => r.risk_level === 'high');
          
          if (unmitigatedRisks.length > 0) {
            addParagraph(`• Risk Management Challenges: ${unmitigatedRisks.length} risks require ongoing attention, including ${highUnmitigatedRisks.length} high-priority items`);
            
            // Challenge categories
            const challengeCategories = unmitigatedRisks.map(r => r.risk_category).filter(Boolean);
            if (challengeCategories.length > 0) {
              const uniqueChallenges = [...new Set(challengeCategories)];
              addParagraph(`• Primary challenge areas: ${uniqueChallenges.join(', ')}`);
            }
          }
        }
        
        // Initiative challenges
        if (initiatives.length > 0) {
          const delayedInitiatives = initiatives.filter(i => 
            i.status === 'in_progress' && 
            i.target_completion_date && 
            new Date(i.target_completion_date) < new Date()
          );
          
          if (delayedInitiatives.length > 0) {
            addParagraph(`• Implementation Challenges: ${delayedInitiatives.length} initiatives experiencing delays, requiring enhanced project management and resource allocation`);
          }
          
          // Resource challenges
          const totalInvestment = initiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
          if (totalInvestment > 0) {
            addParagraph(`• Resource Allocation: Managing $${totalInvestment.toLocaleString()} in sustainability investments requires strategic prioritization and stakeholder alignment`);
          }
        }
        
        // Supply chain challenges
        if (suppliers.length > 0) {
          const nonCompliantSuppliers = suppliers.filter(s => 
            !s.environmental_compliance || !s.social_compliance || !s.governance_compliance
          );
          
          if (nonCompliantSuppliers.length > 0) {
            const complianceRate = suppliers.length > 0 ? ((suppliers.length - nonCompliantSuppliers.length) / suppliers.length * 100).toFixed(1) : '0';
            addParagraph(`• Supply Chain Challenges: ${complianceRate}% compliance rate achieved, with ${nonCompliantSuppliers.length} suppliers requiring enhanced ESG standards implementation`);
          }
        }
        
        // Data and measurement challenges
        const dataGaps = [];
        if (assessments.length === 0) dataGaps.push('baseline emissions data');
        if (initiatives.length === 0) dataGaps.push('initiative impact metrics');
        if (suppliers.length === 0) dataGaps.push('supplier compliance data');
        if (riskAssessments.length === 0) dataGaps.push('risk assessment data');
        
        if (dataGaps.length > 0) {
          addParagraph(`• Data Management Challenges: Gaps identified in ${dataGaps.join(', ')} requiring enhanced data collection and management systems`);
        }
        
        // Lessons learned
        addParagraph('Lessons Learned:');
        addParagraph('• Comprehensive stakeholder engagement is essential for successful ESG program implementation');
        addParagraph('• Data quality and measurement systems must be established early in the sustainability journey');
        addParagraph('• Risk identification and mitigation require ongoing attention and adaptive strategies');
        addParagraph('• Supply chain ESG standards implementation benefits from collaborative partnerships');
        addParagraph('• Continuous improvement and learning are fundamental to long-term sustainability success');
      } else {
        addParagraph('As our sustainability programs develop, we will identify and address challenges to continuously improve our ESG performance and impact.');
      }
      
      addSubTitle('9.2 Strategic Roadmap & Milestones');
      
      // Strategic roadmap development
      const currentYearRoadmap = new Date().getFullYear();
      const roadmapYears = [currentYearRoadmap, currentYearRoadmap + 1, currentYearRoadmap + 2, currentYearRoadmap + 3, currentYearRoadmap + 4, currentYearRoadmap + 5];
      
      addParagraph('5-Year Strategic Sustainability Roadmap:');
      
      // Phase 1: Foundation (Current Year)
      addParagraph(`Phase 1: Foundation (${currentYearRoadmap})`);
      addParagraph('• Establish comprehensive ESG governance framework and policies');
      addParagraph('• Implement data collection and measurement systems');
      addParagraph('• Launch initial sustainability initiatives and pilot programs');
      addParagraph('• Engage stakeholders and build sustainability culture');
      
      // Phase 2: Acceleration (Years 2-3)
      addParagraph(`Phase 2: Acceleration (${currentYearRoadmap + 1}-${currentYearRoadmap + 2})`);
      addParagraph('• Scale successful pilot programs across operations');
      addParagraph('• Enhance supplier ESG standards and compliance');
      addParagraph('• Implement advanced risk management and mitigation strategies');
      addParagraph('• Achieve key sustainability milestones and targets');
      
      // Phase 3: Leadership (Years 4-5)
      addParagraph(`Phase 3: Leadership (${currentYearRoadmap + 3}-${currentYearRoadmap + 4})`);
      addParagraph('• Achieve industry-leading ESG performance');
      addParagraph('• Implement innovative sustainability technologies and solutions');
      addParagraph('• Establish thought leadership and best practice sharing');
      addParagraph('• Contribute to sector-wide sustainability transformation');
      
      // Key milestones
      if (netZeroTargets.length > 0 || userProfile) {
        addParagraph('Key Milestones & Targets:');
        
        // Net zero milestones
        if (userProfile?.net_zero_target_year) {
          const yearsToNetZero = userProfile.net_zero_target_year - currentYearRoadmap;
          addParagraph(`• Net Zero Achievement: ${userProfile.net_zero_target_year} (${yearsToNetZero} years) - Comprehensive decarbonization across all operations`);
        }
        
        if (userProfile?.carbon_neutral_target_year) {
          const yearsToCarbonNeutral = userProfile.carbon_neutral_target_year - currentYearRoadmap;
          addParagraph(`• Carbon Neutrality: ${userProfile.carbon_neutral_target_year} (${yearsToCarbonNeutral} years) - Balance remaining emissions through verified offsets`);
        }
        
        // SBTi milestones
        if (userProfile?.sbti_commitment) {
          if (userProfile?.sbti_validation_date) {
            addParagraph(`• SBTi Validation: ${userProfile.sbti_validation_date} - Science-based targets validated and implementation underway`);
          } else {
            addParagraph('• SBTi Validation: Target 2025 - Complete validation of science-based climate targets');
          }
        }
        
        // Initiative milestones
        if (initiatives.length > 0) {
          const plannedInitiatives = initiatives.filter(i => i.status === 'planned');
          if (plannedInitiatives.length > 0) {
            addParagraph(`• Initiative Pipeline: ${plannedInitiatives.length} planned initiatives to be launched in the next 2-3 years`);
          }
        }
        
        // Standards compliance milestones
        if (selectedStandards.length > 0) {
          addParagraph(`• Standards Excellence: Achieve 90%+ compliance with ${selectedStandards.join(', ')} standards by ${currentYearRoadmap + 2}`);
        }
      }
      
      addSubTitle('9.3 SBTi Pathway & Climate Leadership');
      
      // SBTi pathway analysis
      if (userProfile?.sbti_commitment || netZeroTargets.length > 0) {
        addParagraph('Science Based Targets Initiative (SBTi) Pathway:');
        
        if (userProfile?.sbti_commitment) {
          addParagraph('• SBTi Commitment: Fully committed to science-based climate action aligned with 1.5°C pathway');
          
          if (userProfile?.sbti_validation_date) {
            addParagraph(`• SBTi Validation: Successfully validated on ${userProfile.sbti_validation_date}`);
            addParagraph('• Implementation: Actively implementing validated targets with comprehensive monitoring and reporting');
          } else {
            addParagraph('• SBTi Validation: Target completion in 2025 with comprehensive target development and stakeholder engagement');
          }
        }
        
        // Net zero pathway
        if (userProfile?.net_zero_target_year) {
          const yearsToNetZero = userProfile.net_zero_target_year - currentYearRoadmap;
          const interimTargets = [];
          
          if (yearsToNetZero > 10) {
            interimTargets.push(`${currentYearRoadmap + 5}: 50% emissions reduction`);
          }
          if (yearsToNetZero > 15) {
            interimTargets.push(`${currentYearRoadmap + 10}: 70% emissions reduction`);
          }
          if (yearsToNetZero > 20) {
            interimTargets.push(`${currentYearRoadmap + 15}: 85% emissions reduction`);
          }
          
          addParagraph(`• Net Zero Pathway: ${userProfile.net_zero_target_year} target with interim milestones`);
          if (interimTargets.length > 0) {
            interimTargets.forEach(target => {
              addParagraph(`  - ${target}`);
            });
          }
        }
        
        // Climate leadership vision
        addParagraph('Climate Leadership Vision:');
        addParagraph('• Pioneer innovative decarbonization technologies and solutions');
        addParagraph('• Establish industry-leading emissions reduction practices');
        addParagraph('• Contribute to sector-wide climate transformation');
        addParagraph('• Demonstrate that climate action drives business value and stakeholder benefits');
        
        // Climate innovation pipeline
        const climateInitiatives = initiatives.filter(i => 
          i.category === 'Climate' ||
          i.initiative_name?.toLowerCase().includes('emissions') ||
          i.initiative_name?.toLowerCase().includes('carbon') ||
          i.initiative_name?.toLowerCase().includes('renewable') ||
          i.initiative_name?.toLowerCase().includes('energy efficiency')
        );
        
        if (climateInitiatives.length > 0) {
          const plannedClimate = climateInitiatives.filter(i => i.status === 'planned');
          const activeClimate = climateInitiatives.filter(i => i.status === 'in_progress');
          
          addParagraph(`• Climate Innovation Pipeline: ${climateInitiatives.length} climate-focused initiatives (${activeClimate.length} active, ${plannedClimate.length} planned)`);
        }
      } else {
        addParagraph('SBTi Pathway: We are developing our science-based climate targets and will commit to the SBTi initiative in the coming year.');
        addParagraph('Climate Leadership Vision: Establish comprehensive climate action strategy with ambitious targets aligned with 1.5°C pathway.');
      }
      
      // Future vision and industry leadership
      addParagraph('Future Vision & Industry Leadership:');
      addParagraph('• Transform our industry through sustainable innovation and best practices');
      addParagraph('• Create shared value for all stakeholders through ESG excellence');
      addParagraph('• Contribute to global sustainability goals and climate action');
      addParagraph('• Establish new standards for responsible business leadership');
 
      // 10. Appendices
      addSectionTitle('10. Appendices');
      
      // Enhanced appendices with comprehensive information
      addParagraph('This section provides detailed supporting information including standards mapping, methodology, assurance status, and stakeholder engagement details.');
      
      addSubTitle('10.1 GRI/SASB Standards Index');
      
      // Standards mapping index
      if (selectedStandards.length > 0 && filteredMetrics.length > 0) {
        addParagraph('Standards Compliance Index:');
        
        // GRI Standards mapping
        if (selectedStandards.includes('GRI')) {
          addParagraph('GRI Standards Mapping:');
          
          const griMetrics = filteredMetrics.filter(metric => 
            metric.standards && metric.standards.GRI
          );
          
          if (griMetrics.length > 0) {
            griMetrics.slice(0, 10).forEach(metric => {
              const griCodes = Array.isArray(metric.standards.GRI) ? metric.standards.GRI.join(', ') : metric.standards.GRI;
              addParagraph(`• ${metric.name}: GRI ${griCodes}`);
            });
            
            if (griMetrics.length > 10) {
              addParagraph(`... and ${griMetrics.length - 10} more GRI-aligned metrics`);
            }
          }
        }
        
        // SASB Standards mapping
        if (selectedStandards.includes('SASB')) {
          addParagraph('SASB Standards Mapping:');
          
          const sasbMetrics = filteredMetrics.filter(metric => 
            metric.standards && metric.standards.SASB
          );
          
          if (sasbMetrics.length > 0) {
            sasbMetrics.slice(0, 10).forEach(metric => {
              const sasbCodes = Array.isArray(metric.standards.SASB) ? metric.standards.SASB.join(', ') : metric.standards.SASB;
              addParagraph(`• ${metric.name}: SASB ${sasbCodes}`);
            });
            
            if (sasbMetrics.length > 10) {
              addParagraph(`... and ${sasbMetrics.length - 10} more SASB-aligned metrics`);
            }
          }
        }
        
        // TCFD Framework mapping
        if (selectedStandards.includes('TCFD')) {
          addParagraph('TCFD Framework Mapping:');
          
          const tcfdMetrics = filteredMetrics.filter(metric => 
            metric.standards && metric.standards.TCFD
          );
          
          if (tcfdMetrics.length > 0) {
            tcfdMetrics.slice(0, 10).forEach(metric => {
              const tcfdCodes = Array.isArray(metric.standards.TCFD) ? metric.standards.TCFD.join(', ') : metric.standards.TCFD;
              addParagraph(`• ${metric.name}: TCFD ${tcfdCodes}`);
            });
            
            if (tcfdMetrics.length > 10) {
              addParagraph(`... and ${tcfdMetrics.length - 10} more TCFD-aligned metrics`);
            }
          }
        }
        
        // IFRS S1/S2 mapping
        if (selectedStandards.includes('IFRS S2')) {
          addParagraph('IFRS S1/S2 Climate Standards Mapping:');
          
          const ifrsMetrics = filteredMetrics.filter(metric => 
            metric.standards && (metric.standards.IFRS_S1 || metric.standards.IFRS_S2)
          );
          
          if (ifrsMetrics.length > 0) {
            ifrsMetrics.slice(0, 10).forEach(metric => {
              const ifrsCodes = [];
              if (metric.standards.IFRS_S1) ifrsCodes.push(`IFRS S1: ${metric.standards.IFRS_S1}`);
              if (metric.standards.IFRS_S2) ifrsCodes.push(`IFRS S2: ${metric.standards.IFRS_S2}`);
              addParagraph(`• ${metric.name}: ${ifrsCodes.join(', ')}`);
            });
            
            if (ifrsMetrics.length > 10) {
              addParagraph(`... and ${ifrsMetrics.length - 10} more IFRS-aligned metrics`);
            }
          }
        }
        
        // Standards coverage summary
        const coverageData = generateStandardsCoverage(selectedStandards, filteredMetrics);
        if (coverageData.totalMetrics > 0) {
          addParagraph('Standards Coverage Summary:');
          addParagraph(`• Total Metrics: ${coverageData.totalMetrics} across ${coverageData.totalCategories} categories`);
          
          selectedStandards.forEach(standard => {
            const standardCoverage = coverageData.standards[standard];
            if (standardCoverage) {
              const coveragePercent = Math.round((standardCoverage.metrics / coverageData.totalMetrics) * 100);
              addParagraph(`• ${standard}: ${standardCoverage.metrics} metrics (${coveragePercent}% coverage)`);
            }
          });
        }
      } else {
        addParagraph('Standards mapping index will be generated based on selected reporting standards and available metrics.');
      }
      
      addSubTitle('10.2 Methodology & Data Sources');
      
      // Methodology details
      addParagraph('Data Collection & Measurement Methodology:');
      
      // Data sources
      if (assessments.length > 0 || suppliers.length > 0 || initiatives.length > 0) {
        addParagraph('Primary Data Sources:');
        
        if (assessments.length > 0) {
          addParagraph(`• Environmental Assessments: ${assessments.length} assessments providing emissions, energy, water, and waste data`);
          addParagraph('• Measurement Standards: GHG Protocol, ISO 14064, and industry-specific methodologies');
          addParagraph('• Data Quality: Third-party verification and internal validation processes');
        }
        
        if (suppliers.length > 0) {
          addParagraph(`• Supplier Data: ${suppliers.length} suppliers providing compliance and performance information`);
          addParagraph('• Assessment Framework: Comprehensive ESG compliance evaluation and risk assessment');
          addParagraph('• Continuous Monitoring: Regular supplier audits and performance tracking');
        }
        
        if (initiatives.length > 0) {
          addParagraph(`• Initiative Tracking: ${initiatives.length} sustainability initiatives with impact measurement`);
          addParagraph('• ROI Calculation: Standard financial return on investment methodology');
          addParagraph('• Impact Quantification: Emissions reduction, efficiency gains, and social impact metrics');
        }
        
        if (riskAssessments.length > 0) {
          addParagraph(`• Risk Assessment: ${riskAssessments.length} risks identified and assessed using industry-standard frameworks`);
          addParagraph('• Risk Methodology: ISO 31000 risk management principles and best practices');
          addParagraph('• Mitigation Tracking: Implementation status and effectiveness monitoring');
        }
        
        if (investments.length > 0) {
          addParagraph(`• Investment Analysis: ${investments.length} investments evaluated using ESG scoring frameworks`);
          addParagraph('• ESG Scoring: Multi-dimensional environmental, social, and governance assessment');
          addParagraph('• Portfolio Management: Diversification and risk-adjusted return analysis');
        }
      } else {
        addParagraph('Data collection and measurement methodology will be detailed as sustainability programs are implemented and data systems established.');
      }
      
      // Calculation methodologies
      addParagraph('Calculation Methodologies:');
      addParagraph('• GHG Emissions: IPCC guidelines and GHG Protocol standards');
      addParagraph('• Energy Efficiency: Industry benchmarks and best practice comparisons');
      addParagraph('• Water Management: Local water stress assessments and conservation metrics');
      addParagraph('• Waste Management: Circular economy principles and diversion rate calculations');
      addParagraph('• Social Impact: Stakeholder engagement and community benefit quantification');
      addParagraph('• Financial Performance: Standard accounting principles and ROI calculations');
      
      addSubTitle('10.3 Assurance & Verification Status');
      
      // Assurance and verification information
      addParagraph('Data Quality & Assurance:');
      
      // Internal controls
      addParagraph('Internal Controls:');
      addParagraph('• Data validation and quality assurance processes');
      addParagraph('• Regular internal audits and compliance monitoring');
      addParagraph('• Stakeholder review and feedback integration');
      addParagraph('• Continuous improvement and methodology refinement');
      
      // External verification
      if (userProfile?.sbti_commitment) {
        addParagraph('External Verification:');
        addParagraph('• Science Based Targets initiative validation and ongoing monitoring');
        if (userProfile?.sbti_validation_date) {
          addParagraph(`• SBTi validation completed on ${userProfile.sbti_validation_date}`);
        }
        addParagraph('• Regular SBTi progress reporting and verification');
      }
      
      // Standards compliance
      if (selectedStandards.length > 0) {
        addParagraph('Standards Compliance:');
        addParagraph(`• Alignment with ${selectedStandards.join(', ')} reporting requirements`);
        addParagraph('• Regular standards updates and compliance monitoring');
        addParagraph('• Stakeholder feedback and continuous improvement');
      }
      
      // Future assurance plans
      addParagraph('Future Assurance Plans:');
      addParagraph('• Third-party verification of key sustainability metrics');
      addParagraph('• Enhanced data quality scoring and validation');
      addParagraph('• Stakeholder assurance and confidence building');
      addParagraph('• Industry leadership in ESG reporting excellence');
      
      addSubTitle('10.4 Feedback & Contact Information');
      
      // Enhanced contact information
      if (userProfile) {
        const contactInfo = getContactInfo(userProfile);
        
        addParagraph('For questions about this report or our sustainability initiatives, please contact us:');
        
        // Organization details
        addParagraph(`Organization: ${contactInfo.name}`);
        if (contactInfo.location) addParagraph(`Location: ${contactInfo.location}`);
        if (contactInfo.website) addParagraph(`Website: ${contactInfo.website}`);
        if (contactInfo.email) addParagraph(`Email: ${contactInfo.email}`);
        if (contactInfo.phone) addParagraph(`Phone: ${contactInfo.phone}`);
        
        // Additional contact information
        if (userProfile.department) addParagraph(`Department: ${userProfile.department}`);
        if (userProfile.job_title) addParagraph(`Contact Person: ${userProfile.full_name || 'Sustainability Team'} (${userProfile.job_title})`);
        
        // Stakeholder engagement
        addParagraph('Stakeholder Engagement:');
        addParagraph('• We welcome feedback and suggestions for improving our ESG reporting');
        addParagraph('• Regular stakeholder consultation and engagement sessions');
        addParagraph('• Continuous improvement based on stakeholder input and best practices');
        addParagraph('• Transparent communication and open dialogue on sustainability topics');
        
        // Report feedback
        addParagraph('Report Feedback:');
        addParagraph('• Report accessibility and clarity feedback welcome');
        addParagraph('• Suggestions for additional metrics or reporting areas');
        addParagraph('• Stakeholder information needs and priorities');
        addParagraph('• Continuous improvement opportunities');
      } else {
        addParagraph('For questions about this report or our sustainability initiatives, please contact our sustainability team.');
        addParagraph('Stakeholder Engagement: We welcome feedback and suggestions for improving our ESG reporting and sustainability performance.');
      }
      
      // Glossary
      addParagraph('Glossary:');
      addParagraph('• ESG: Environmental, Social, and Governance factors');
      addParagraph('• GHG: Greenhouse Gas emissions (CO2, CH4, N2O, etc.)');
      addParagraph('• tCO2e: Metric tons of carbon dioxide equivalent');
      addParagraph('• SBTi: Science Based Targets initiative');
      addParagraph('• ROI: Return on Investment');
      addParagraph('• Scope 1: Direct emissions from owned or controlled sources');
      addParagraph('• Scope 2: Indirect emissions from purchased energy');
      addParagraph('• Scope 3: Indirect emissions from value chain activities');
 
      // Footer for each page
      const addFooter = () => {
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();
        pdf.setDrawColor(229); pdf.setLineWidth(0.5); pdf.line(12, ph - 14, pw - 12, ph - 14);
        pdf.setFontSize(9); pdf.setTextColor(120);
        pdf.text('Generated with GreenCard Sustainability Manager', 14, ph - 8);
        pdf.text(new Date().toLocaleString(), pw - 14, ph - 8, { align: 'right' });
      };
      const totalPages = (pdf as any).internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) { pdf.setPage(i); addFooter(); }
 
      pdf.save(`${reportName || 'ESG-Report'}.pdf`);
    } catch (e) {
      toast({ title: 'Failed to export report', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            ESG Report Builder
          </CardTitle>
          <p className="text-gray-600">
            Create comprehensive sustainability reports with multi-standard compliance and live data preview
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Report Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="reportName">Report Name</Label>
                <Input
                  id="reportName"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder="e.g., 2024 Annual Sustainability Report"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Standards Selection */}
            <div>
              <Label>Reporting Standards</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {standards.map(standard => (
                  <div key={standard} className="flex items-center space-x-2">
                    <Checkbox 
                      id={standard}
                      checked={selectedStandards.includes(standard)}
                      onCheckedChange={(checked) => handleStandardChange(standard, !!checked)}
                    />
                    <Label htmlFor={standard} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{standard}</Badge>
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Separator />

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>ESG Categories</Label>
              <div className="space-y-2 mt-2">
                {categories.map(category => (
                  <div key={category} className="flex items-center space-x-2">
                    <Checkbox 
                      id={category}
                      checked={selectedCategories.includes(category)}
                      onCheckedChange={(checked) => handleCategoryChange(category, !!checked)}
                    />
                    <Label htmlFor={category} className="flex items-center gap-2">
                      {getCategoryIcon(category)}
                      {category}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Carbon Scopes</Label>
              <div className="space-y-2 mt-2">
                {scopes.map(scope => (
                  <div key={scope} className="flex items-center space-x-2">
                    <Checkbox 
                      id={scope}
                      checked={selectedScopes.includes(scope)}
                      onCheckedChange={(checked) => handleScopeChange(scope, !!checked)}
                    />
                    <Label htmlFor={scope}>{scope}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex gap-4">
            <Button onClick={handleGenerateReport} className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Generate Report Preview
            </Button>
            <Button variant="outline" className="flex items-center gap-2" onClick={generatePDF}>
              <Download className="h-4 w-4" />
              Export Report (PDF)
            </Button>
          </div>

          {/* Metrics Preview */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">
              Report will include {filteredMetrics.length} metrics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMetrics.slice(0, 6).map((metric: any) => (
                <Card key={metric.id} className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {getCategoryIcon(metric.category) as React.ReactNode}
                    <span className="font-medium text-sm">{metric.name}</span>
                  </div>
                  <div className="text-xs text-gray-600 mb-2">{metric.subcategory}</div>
                  <div className="flex flex-wrap gap-1">
                    {metric.standards && Object.entries(metric.standards).slice(0, 2).map(([standardName, codes], index) => (
                      Array.isArray(codes) ? codes.slice(0, 1).map((code, codeIndex) => (
                        <Badge key={`${standardName}-${index}-${codeIndex}`} variant="outline" className="text-xs">
                          {standardName} {code}
                        </Badge>
                      )) : (
                        <Badge key={`${standardName}-${index}`} variant="outline" className="text-xs">
                          {standardName} {String(codes)}
                        </Badge>
                      )
                    ))}
                  </div>
                </Card>
              ))}
            </div>
            {filteredMetrics.length > 6 && (
              <p className="text-sm text-gray-600 mt-2">
                And {filteredMetrics.length - 6} more metrics...
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Live Preview */}
      {showPreview && (
        <Card>
          <CardHeader>
            <CardTitle>Report Preview</CardTitle>
            <p className="text-gray-600">
              Live preview based on your organization's data for {startDate} to {endDate}
            </p>
          </CardHeader>
          <CardContent>
            {/* User Profile Preview */}
            {userProfile && (
              <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                <h3 className="text-lg font-semibold mb-3">Organization Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Organization Name</p>
                    <p className="text-gray-900">{userProfile.company_name}</p>
                  </div>
                  {userProfile.industry_sector && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Industry Sector</p>
                      <p className="text-gray-900">{userProfile.industry_sector}</p>
                    </div>
                  )}
                  {userProfile.company_size && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Company Size</p>
                      <p className="text-gray-900">{userProfile.company_size}</p>
                    </div>
                  )}
                  {userProfile.headquarters_location && (
                    <div>
                      <p className="text-sm font-medium text-gray-700">Headquarters</p>
                      <p className="text-gray-900">{userProfile.headquarters_location}</p>
                    </div>
                  )}
                </div>
                
                {userProfile.mission_statement && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700">Mission Statement</p>
                    <p className="text-gray-900 text-sm">{userProfile.mission_statement}</p>
                  </div>
                )}
                
                {userProfile.esg_policy && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700">ESG Policy</p>
                    <p className="text-gray-900 text-sm">{userProfile.esg_policy}</p>
                  </div>
                )}
                
                <div className="mt-4 flex flex-wrap gap-2">
                  {userProfile.net_zero_target_year && (
                    <Badge variant="outline">Net Zero: {userProfile.net_zero_target_year}</Badge>
                  )}
                  {userProfile.sbti_commitment && (
                    <Badge variant="outline">SBTi Committed</Badge>
                  )}
                  {userProfile.carbon_neutral_commitment && (
                    <Badge variant="outline">Carbon Neutral</Badge>
                  )}
                </div>
              </div>
            )}
            
            {/* ESG Insights Preview */}
            {esgInsights && (
              <div className="mb-6 p-4 border rounded-lg bg-blue-50">
                <h3 className="text-lg font-semibold mb-3">Generated ESG Insights</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Company Overview</p>
                    <p className="text-gray-900 text-sm">{esgInsights.companyOverview}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Key Achievements</p>
                    <p className="text-gray-900 text-sm">{esgInsights.keyAchievements}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Future Goals</p>
                    <p className="text-gray-900 text-sm">{esgInsights.futureGoals}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Key Achievements Preview */}
            <div className="mb-6 p-4 border rounded-lg bg-green-50">
              <h3 className="text-lg font-semibold mb-3">Key Achievements & Progress</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{suppliers.length}</p>
                  <p className="text-sm text-gray-600">Total Suppliers</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{assessments.filter(a => a.status === 'completed').length}</p>
                  <p className="text-sm text-gray-600">Completed Assessments</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{initiatives.filter(i => i.status !== 'completed').length}</p>
                  <p className="text-sm text-gray-600">Initiatives In Progress</p>
                </div>
              </div>
              
              {/* Enhanced Metrics */}
              {(() => {
                const completedInitiatives = initiatives.filter(i => i.status === 'completed');
                const totalReduction = completedInitiatives.reduce((sum, i) => sum + (i.actual_reduction || 0), 0);
                const totalInvestment = completedInitiatives.reduce((sum, i) => sum + (i.investment_required || 0), 0);
                const avgROI = completedInitiatives.length > 0 ? 
                  completedInitiatives.reduce((sum, i) => sum + (i.roi_percentage || 0), 0) / completedInitiatives.length : 0;
                
                if (completedInitiatives.length > 0) {
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{totalReduction.toLocaleString()}</p>
                        <p className="text-sm text-gray-600">tCO2e Reduced</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">${totalInvestment.toLocaleString()}</p>
                        <p className="text-sm text-gray-600">Total Investment</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">{avgROI.toFixed(1)}%</p>
                        <p className="text-sm text-gray-600">Average ROI</p>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Top Achievements */}
              {(() => {
                const completedInitiatives = initiatives.filter(i => i.status === 'completed');
                if (completedInitiatives.length > 0) {
                  const topAchievements = completedInitiatives
                    .sort((a, b) => (b.actual_reduction || 0) - (a.actual_reduction || 0))
                    .slice(0, 3);
                  
                  return (
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Top 3 Achievements</h4>
                      <div className="space-y-2">
                        {topAchievements.map((initiative, index) => (
                          <div key={initiative.id} className="flex items-center justify-between p-2 bg-white rounded">
                            <span className="font-medium">{index + 1}. {initiative.initiative_name}</span>
                            <span className="text-sm text-gray-600">
                              {initiative.actual_reduction?.toLocaleString() || 0} tCO2e
                              {initiative.roi_percentage && ` (${initiative.roi_percentage.toFixed(1)}% ROI)`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Progress Indicators */}
              <div>
                <h4 className="font-semibold mb-2">Progress Indicators</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-sm text-gray-600">Supplier Compliance Rate</p>
                    <p className="text-lg font-semibold">
                      {(() => {
                        const compliance = suppliers.length ? 
                          suppliers.filter(s => s.environmental_compliance && s.social_compliance && s.governance_compliance).length / suppliers.length * 100 : 0;
                        return `${compliance.toFixed(1)}%`;
                      })()}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-sm text-gray-600">Initiative Completion Rate</p>
                    <p className="text-lg font-semibold">
                      {(() => {
                        const completed = initiatives.filter(i => i.status === 'completed').length;
                        const total = initiatives.length;
                        const rate = total > 0 ? (completed / total) * 100 : 0;
                        return `${rate.toFixed(1)}%`;
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <ScrollArea className="h-96">
              <div className="space-y-4">
                {filteredMetrics.map((metric: any) => {
                  const userValue = userDataPoints.find(dp => dp.metric_name === metric.name);
                  return (
                    <div key={metric.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium">{metric.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{metric.description}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {metric.standards && Object.entries(metric.standards).map(([standardName, codes], index) => (
                              Array.isArray(codes) ? codes.map((code, codeIndex) => (
                                <Badge key={`${standardName}-${index}-${codeIndex}`} variant="outline" className="text-xs">
                                  {standardName} {code}
                                </Badge>
                              )) : (
                                <Badge key={`${standardName}-${index}`} variant="outline" className="text-xs">
                                  {standardName} {String(codes)}
                                </Badge>
                              )
                            ))}
                          </div>
                        </div>
                        <div className="ml-4 text-right">
                          <div className="text-lg font-semibold">
                            {userValue?.metric_value || 'No data'}
                          </div>
                          <div className="text-sm text-gray-600">{metric.unit}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      <Dialog open={showEnterpriseUpgradeModal} onOpenChange={setShowEnterpriseUpgradeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enterprise Plan Required</DialogTitle>
            <DialogDescription>
              ESG Reporting preview and PDF export are available on the Enterprise plan only.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border bg-slate-50 p-4 text-sm text-slate-700">
            Upgrade to Enterprise to access report preview generation and PDF export.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowEnterpriseUpgradeModal(false)}>
              Close
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                setShowEnterpriseUpgradeModal(false);
                navigate('/settings?tab=advanced&upgradePlan=enterprise');
              }}
            >
              Upgrade To Enterprise
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden chart container for PDF capture */}
      <div className="absolute left-[-9999px] top-[-9999px] w-[800px]">
        <div ref={emissionsChartRef} className="p-4 bg-white">
          <Label>Emissions by Scope (tCO2e)</Label>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={emissionsByScope}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#22c55e" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div ref={waterWasteChartRef} className="p-4 bg-white">
          <Label>Water & Waste</Label>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie dataKey="value" data={waterWasteData} cx="50%" cy="50%" outerRadius={90} label>
                {waterWasteData.map((entry, index) => (
                  <Cell key={`cell-w-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div ref={riskChartRef} className="p-4 bg-white">
          <Label>Risk Categories</Label>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={riskCategoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div ref={investmentChartRef} className="p-4 bg-white">
          <Label>Avg ESG by Sector</Label>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={investmentESG}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#a855f7" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
